import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'client/axios';
import { instanceSetupUpdate as instanceSave } from 'client/redux/instance/setupActions';
import { INSTANCE_SETTINGS_UPDATE } from 'client/redux/instance/constants';
import { instanceSettingsUpdate as instanceSettingsSave } from 'client/redux/instance/settingsActions';
import BeRemoteErrorAlert from 'client/components/Be/BeRemoteErrorAlert';
import Select2Wrap from 'client/components/Form/Select2Wrap';
import { EndpointTopdeskRemoteSearcher } from 'client/components/Form/Select2Searchers';
import { getEatdActiveTab, setEatdActiveTab } from 'client/components/EATD/tabState';

const EATD_OPTIONS_FORM_STYLES = `
  .eatd-options-form {
    width: 100%;
    margin: 0 auto;
    padding-bottom: 24px;
  }

  .eatd-tab-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px;
    margin-bottom: 24px;
    padding: 0 0 18px;
    border-bottom: 1px solid #d7dee8;
  }

  .eatd-tab-button {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #c9d4e3;
    border-radius: 999px;
    background: #f7f9fc;
    color: #455468;
    font-weight: 600;
    min-height: 44px;
    padding: 10px 16px;
    text-align: center;
    transition: all 0.18s ease;
  }

  .eatd-tab-button:hover:not(:disabled) {
    background: #eef4fb;
    border-color: #b8c7dc;
    color: #243447;
  }

  .eatd-tab-button.is-active,
  .eatd-tab-button:disabled {
    background: #1f6feb;
    border-color: #1f6feb;
    color: #ffffff;
    cursor: default;
    opacity: 1;
    box-shadow: 0 8px 18px rgba(31, 111, 235, 0.16);
  }

  .eatd-tab-panel {
    background: #f8fafc;
    border: 1px solid #d7dee8;
    border-radius: 16px;
    padding: 24px;
  }

  .eatd-section-card {
    background: #ffffff;
    border: 1px solid #d7dee8;
    border-radius: 14px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
    padding: 20px;
  }

  .eatd-section-card + .eatd-section-card {
    margin-top: 20px;
  }

  .eatd-section-card--soft {
    background: linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
  }

  .eatd-section-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 16px;
  }

  .eatd-section-title {
    margin: 0;
    color: #1f2937;
    font-weight: 600;
  }

  .eatd-section-description {
    margin: 6px 0 0;
    color: #6b7280;
    max-width: 680px;
  }

  .eatd-profile-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 16px;
  }

  .eatd-profile-tab {
    border: 1px solid #cad5e3;
    border-radius: 10px;
    background: #ffffff;
    color: #455468;
    font-weight: 600;
    padding: 8px 14px;
  }

  .eatd-profile-tab.is-active {
    background: #eff6ff;
    border-color: #60a5fa;
    color: #1d4ed8;
    box-shadow: inset 0 0 0 1px rgba(96, 165, 250, 0.2);
  }

  .eatd-profile-shell {
    border: 1px solid #d7dee8;
    border-radius: 12px;
    background: #f8fafc;
    padding: 18px;
  }

  .eatd-profile-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }

  .eatd-profile-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }

  .eatd-subsection {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #ffffff;
    padding: 16px;
  }

  .eatd-subsection + .eatd-subsection {
    margin-top: 16px;
  }

  .eatd-subsection-title {
    margin: 0 0 6px;
    font-size: 14px;
    font-weight: 600;
    color: #334155;
  }

  .eatd-subsection-description {
    margin: 0 0 14px;
    color: #6b7280;
    font-size: 13px;
  }

  .eatd-rule-item {
    background: #f8fafc;
    border: 1px solid #dbe4ef;
    border-radius: 12px;
    padding: 12px;
  }

  .eatd-action-button {
    white-space: nowrap;
  }

  .eatd-action-button + .eatd-action-button {
    margin-left: 8px;
  }

  .eatd-inline-action {
    margin-top: 12px;
  }

  .eatd-inline-action--spaced {
    margin-bottom: 8px;
  }

  .eatd-rule-card .card-header {
    padding: 12px 16px;
  }

  .eatd-rule-card .card-body {
    padding: 16px;
  }

  .eatd-compact-note {
    max-width: 620px;
  }

  .eatd-profile-detail-row {
    margin-top: 8px;
  }

  .eatd-save-bar {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid #d7dee8;
  }

  @media (max-width: 767px) {
    .eatd-tab-panel {
      padding: 16px;
    }

    .eatd-section-card {
      padding: 16px;
    }

    .eatd-section-header,
    .eatd-profile-heading {
      flex-direction: column;
      align-items: stretch;
    }

    .eatd-save-bar {
      justify-content: stretch;
    }

    .eatd-save-bar .btn {
      width: 100%;
    }
  }
`;

const buildOptionData = values => values.map(value => ({ id: value, text: value, value }));

const createFieldDefinition = (field, label, inputType = 'text', options = [], meta = {}) => ({
  field,
  label,
  inputType,
  options,
  ...meta,
});

const buildFieldList = definitions => definitions.map(definition => definition.field);

const buildSelect2Data = definitions => definitions.map(definition => ({
  id: definition.field,
  text: definition.label,
}));

const buildOptionSignature = options => (Array.isArray(options)
  ? options.map(option => String(option.id ?? option.value ?? option.text ?? '')).join('|')
  : '');

const getFieldDefinitionFromCatalog = (catalog, field) => catalog.find(definition => definition.field === field) || null;

const normalizeValueForField = (fieldDefinition, value) => {
  if (!fieldDefinition || value === undefined || value === null || value === '') {
    return value ?? '';
  }

  if (fieldDefinition.inputType === 'number') {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : value;
  }

  return value;
};

const TOPDESK_CONDITION_OPTION_SOURCES = {
  categories: { responseProp: 'categories' },
  subcategories: { responseProp: 'subcategories' },
  callTypes: { responseProp: 'callTypes' },
  entryTypes: { responseProp: 'entryTypes' },
  processingStatus: { responseProp: 'processingStatus' },
  operatorGroups: { responseProp: 'operatorGroups' },
  suppliers: { responseProp: 'suppliers' },
};

const TOPDESK_EQ_ONLY_OPERATORS = ['==', '!='];

const getOperatorsForFieldDefinition = fieldDefinition => {
  if (!fieldDefinition) return OPERATORS;
  if (fieldDefinition.inputType === 'enum' || fieldDefinition.inputType === 'topdeskSelect') {
    return TOPDESK_EQ_ONLY_OPERATORS;
  }
  return OPERATORS;
};

const INCIDENT_STATUS_OPTIONS = buildOptionData(['firstLine', 'secondLine', 'partial']);
const CHANGE_TYPE_OPTIONS = buildOptionData(['standard', 'nonStandard']);
const CHANGE_IMPACT_OPTIONS = buildOptionData(['person', 'team', 'department', 'site']);

const OPERATOR_WHEN_FIELD_DEFINITIONS = [
  createFieldDefinition('surName', 'Surname'),
  createFieldDefinition('firstName', 'First Name'),
  createFieldDefinition('firstInitials', 'First Initials'),
  createFieldDefinition('prefixes', 'Prefixes'),
  createFieldDefinition('birthName', 'Birth Name'),
  createFieldDefinition('title', 'Title'),
  createFieldDefinition('telephone', 'Telephone'),
  createFieldDefinition('mobileNumber', 'Mobile Number'),
  createFieldDefinition('faxNumber', 'Fax Number'),
  createFieldDefinition('email', 'Email'),
  createFieldDefinition('exchangeAccount', 'Exchange Account'),
  createFieldDefinition('jobTitle', 'Job Title'),
  createFieldDefinition('employeeNumber', 'Employee Number'),
  createFieldDefinition('networkLoginName', 'Network Login Name'),
  createFieldDefinition('mainframeLoginName', 'Mainframe Login Name'),
  createFieldDefinition('loginName', 'Login Name'),
  createFieldDefinition('password', 'Password'),
  createFieldDefinition('comments', 'Comments'),
  createFieldDefinition('optionalFields1.number1', 'Optional Number 1.1', 'number'),
  createFieldDefinition('optionalFields1.number2', 'Optional Number 1.2', 'number'),
  createFieldDefinition('optionalFields1.number3', 'Optional Number 1.3', 'number'),
  createFieldDefinition('optionalFields1.number4', 'Optional Number 1.4', 'number'),
  createFieldDefinition('optionalFields1.number5', 'Optional Number 1.5', 'number'),
  createFieldDefinition('optionalFields1.text1', 'Optional Text 1.1'),
  createFieldDefinition('optionalFields1.text2', 'Optional Text 1.2'),
  createFieldDefinition('optionalFields1.text3', 'Optional Text 1.3'),
  createFieldDefinition('optionalFields1.text4', 'Optional Text 1.4'),
  createFieldDefinition('optionalFields1.text5', 'Optional Text 1.5'),
  createFieldDefinition('optionalFields2.number1', 'Optional Number 2.1', 'number'),
  createFieldDefinition('optionalFields2.number2', 'Optional Number 2.2', 'number'),
  createFieldDefinition('optionalFields2.number3', 'Optional Number 2.3', 'number'),
  createFieldDefinition('optionalFields2.number4', 'Optional Number 2.4', 'number'),
  createFieldDefinition('optionalFields2.number5', 'Optional Number 2.5', 'number'),
  createFieldDefinition('optionalFields2.text1', 'Optional Text 2.1'),
  createFieldDefinition('optionalFields2.text2', 'Optional Text 2.2'),
  createFieldDefinition('optionalFields2.text3', 'Optional Text 2.3'),
  createFieldDefinition('optionalFields2.text4', 'Optional Text 2.4'),
  createFieldDefinition('optionalFields2.text5', 'Optional Text 2.5'),
];

const OPERATOR_THEN_FIELD_DEFINITIONS = OPERATOR_WHEN_FIELD_DEFINITIONS.filter(definition => definition.inputType === 'text');

const PERSON_WHEN_FIELD_DEFINITIONS = [
  createFieldDefinition('id', 'ID'),
  createFieldDefinition('firstName', 'First Name'),
  createFieldDefinition('surName', 'Surname'),
  createFieldDefinition('dynamicName', 'Dynamic Name'),
  createFieldDefinition('firstInitials', 'First Initials'),
  createFieldDefinition('prefixes', 'Prefixes'),
  createFieldDefinition('birthName', 'Birth Name'),
  createFieldDefinition('title', 'Title'),
  createFieldDefinition('phoneNumber', 'Phone Number'),
  createFieldDefinition('mobileNumber', 'Mobile Number'),
  createFieldDefinition('fax', 'Fax'),
  createFieldDefinition('email', 'Email'),
  createFieldDefinition('jobTitle', 'Job Title'),
  createFieldDefinition('employeeNumber', 'Employee Number'),
  createFieldDefinition('networkLoginName', 'Network Login Name'),
  createFieldDefinition('mainframeLoginName', 'Mainframe Login Name'),
  createFieldDefinition('city', 'City'),
  createFieldDefinition('tasLoginName', 'TAS Login Name'),
  createFieldDefinition('privateDetails.address.street', 'Private Street'),
  createFieldDefinition('privateDetails.address.number', 'Private House Number'),
  createFieldDefinition('privateDetails.address.county', 'Private County'),
  createFieldDefinition('privateDetails.address.city', 'Private Address City'),
  createFieldDefinition('privateDetails.address.postcode', 'Private Postcode'),
  createFieldDefinition('privateDetails.address.addressMemo', 'Private Address Memo'),
  createFieldDefinition('privateDetails.address.addressType', 'Private Address Type'),
  createFieldDefinition('privateDetails.identification.socialSecurityNumber', 'Social Security Number'),
  createFieldDefinition('privateDetails.identification.idNumber', 'ID Number'),
  createFieldDefinition('privateDetails.identification.issueDate', 'ID Issue Date'),
  createFieldDefinition('privateDetails.identification.expiryDate', 'ID Expiry Date'),
  createFieldDefinition('privateDetails.identification.issuePlace', 'ID Issue Place'),
  createFieldDefinition('privateDetails.identification.bankAccount', 'Bank Account'),
  createFieldDefinition('privateDetails.privateContactDetails.phoneNumber1', 'Private Phone Number 1'),
  createFieldDefinition('privateDetails.privateContactDetails.phoneNumber2', 'Private Phone Number 2'),
  createFieldDefinition('privateDetails.privateContactDetails.mobileNumber', 'Private Mobile Number'),
  createFieldDefinition('privateDetails.privateContactDetails.fax', 'Private Fax'),
  createFieldDefinition('privateDetails.privateContactDetails.email', 'Private Email'),
  createFieldDefinition('privateDetails.birthDetails.dateOfBirth', 'Date Of Birth'),
  createFieldDefinition('privateDetails.birthDetails.placeOfBirth', 'Place Of Birth'),
  createFieldDefinition('privateDetails.emergencyContact.name', 'Emergency Contact Name'),
  createFieldDefinition('privateDetails.emergencyContact.relation', 'Emergency Contact Relation'),
  createFieldDefinition('privateDetails.emergencyContact.telephoneNumber', 'Emergency Contact Telephone'),
];

const PERSON_THEN_FIELD_DEFINITIONS = PERSON_WHEN_FIELD_DEFINITIONS.filter(definition => ![
  'id',
].includes(definition.field));

const INCIDENT_WHEN_FIELD_DEFINITIONS = [
  createFieldDefinition('number', 'Incident Number'),
  createFieldDefinition('caller', 'Caller', 'reference'),
  createFieldDefinition('caller.dynamicName', 'Caller Name'),
  createFieldDefinition('caller.id', 'Caller ID'),
  createFieldDefinition('briefDescription', 'Brief Description'),
  createFieldDefinition('externalNumber', 'External Number'),
  createFieldDefinition('category.name', 'Category', 'topdeskSelect', [], { topdeskOptionSource: 'categories' }),
  createFieldDefinition('subcategory.name', 'Subcategory', 'topdeskSelect', [], { topdeskOptionSource: 'subcategories' }),
  createFieldDefinition('callType.name', 'Call Type', 'topdeskSelect', [], { topdeskOptionSource: 'callTypes' }),
  createFieldDefinition('entryType.name', 'Entry Type', 'topdeskSelect', [], { topdeskOptionSource: 'entryTypes' }),
  createFieldDefinition('status', 'Status', 'enum', INCIDENT_STATUS_OPTIONS),
  createFieldDefinition('operator.name', 'Operator Name', 'reference'),
  createFieldDefinition('operatorGroup.name', 'Operator Group Name', 'topdeskSelect', [], { topdeskOptionSource: 'operatorGroups' }),
  createFieldDefinition('supplier.name', 'Supplier Name', 'topdeskSelect', [], { topdeskOptionSource: 'suppliers' }),
  createFieldDefinition('processingStatus.name', 'Processing Status', 'topdeskSelect', [], { topdeskOptionSource: 'processingStatus' }),
  createFieldDefinition('priority.name', 'Priority', 'reference'),
  createFieldDefinition('duration', 'Duration', 'number'),
  createFieldDefinition('resolution', 'Resolution'),
  createFieldDefinition('closureCode.name', 'Closure Code', 'reference'),
  createFieldDefinition('feedbackScore', 'Feedback Score', 'number'),
  createFieldDefinition('feedbackText', 'Feedback Text'),
];

const INCIDENT_THEN_FIELD_DEFINITIONS = [
  createFieldDefinition('caller', 'Caller', 'reference'),
  createFieldDefinition('briefDescription', 'Brief Description'),
  createFieldDefinition('externalNumber', 'External Number'),
  createFieldDefinition('resolution', 'Resolution'),
  createFieldDefinition('feedbackText', 'Feedback Text'),
];

const CHANGE_WHEN_FIELD_DEFINITIONS = [
  createFieldDefinition('id', 'ID'),
  createFieldDefinition('number', 'Change Number'),
  createFieldDefinition('requester', 'Requester (Person)', 'reference'),
  createFieldDefinition('operator', 'Operator (Coordinator)', 'reference'),
  createFieldDefinition('operatorGroup', 'Operator Group', 'topdeskSelect', [], { topdeskOptionSource: 'operatorGroups' }),
  createFieldDefinition('briefDescription', 'Brief Description'),
  createFieldDefinition('request', 'Request'),
  createFieldDefinition('action', 'Action'),
  createFieldDefinition('category', 'Category', 'topdeskSelect', [], { topdeskOptionSource: 'categories' }),
  createFieldDefinition('subCategory', 'Subcategory', 'topdeskSelect', [], { topdeskOptionSource: 'subcategories' }),
  createFieldDefinition('externalNumber', 'External Number'),
  createFieldDefinition('changeType', 'Change Type', 'enum', CHANGE_TYPE_OPTIONS),
  createFieldDefinition('impact', 'Impact', 'enum', CHANGE_IMPACT_OPTIONS),
  createFieldDefinition('benefit', 'Benefit'),
  createFieldDefinition('priority', 'Priority', 'reference'),
  createFieldDefinition('status', 'Status'),
  createFieldDefinition('processingStatus', 'Processing Status', 'topdeskSelect', [], { topdeskOptionSource: 'processingStatus' }),
];

const CHANGE_THEN_FIELD_DEFINITIONS = [
  createFieldDefinition('requester', 'Requester (Person)', 'reference'),
  createFieldDefinition('operator', 'Operator (Coordinator)', 'reference'),
  createFieldDefinition('operatorGroup', 'Operator Group', 'reference'),
  createFieldDefinition('briefDescription', 'Brief Description'),
  createFieldDefinition('externalNumber', 'External Number'),
];

const FIELD_CATALOGS = {
  operator: {
    conditions: OPERATOR_WHEN_FIELD_DEFINITIONS,
    changes: OPERATOR_THEN_FIELD_DEFINITIONS,
  },
  person: {
    conditions: PERSON_WHEN_FIELD_DEFINITIONS,
    changes: PERSON_THEN_FIELD_DEFINITIONS,
  },
  incident: {
    conditions: INCIDENT_WHEN_FIELD_DEFINITIONS,
    changes: INCIDENT_THEN_FIELD_DEFINITIONS,
  },
  change: {
    conditions: CHANGE_WHEN_FIELD_DEFINITIONS,
    changes: CHANGE_THEN_FIELD_DEFINITIONS,
  },
};

const getFieldDefinitions = (ruleType, section) => _.get(FIELD_CATALOGS, `${ruleType}.${section}`, []);

const getFieldList = (ruleType, section) => buildFieldList(getFieldDefinitions(ruleType, section));

const getFieldDefinition = (ruleType, section, field) => getFieldDefinitionFromCatalog(getFieldDefinitions(ruleType, section), field);

const getFieldSelectData = (ruleType, section) => buildSelect2Data(getFieldDefinitions(ruleType, section));


/**
 * @constant OPERATORS
 * @description Array of available comparison operators for conditions.
 */
const OPERATORS = ['==', '!=', '=sw=', '<', '>']; // '=sw=' could mean 'starts with'

/**
 * @constant DEFAULT_OPERATOR_CONDITION
 * @description Default structure for a new operator condition.
 */
const DEFAULT_OPERATOR_CONDITION = {
  field: getFieldList('operator', 'conditions')[0] || '',
  operator: OPERATORS[0] || '==',
  value: '',
};

/**
 * @constant DEFAULT_OPERATOR_CHANGE
 * @description Default structure for a new operator change.
 */
const DEFAULT_OPERATOR_CHANGE = {
  field: getFieldList('operator', 'changes')[0] || '',
  value: '',
};

/**
 * @constant DEFAULT_OPERATOR_RULE
 * @description Default structure for a new operator rule.
 */
const DEFAULT_OPERATOR_RULE = {
  conditions: [],
  changes: [],
};

// --- BEGIN: Added Person Defaults ---
/**
 * @constant DEFAULT_PERSON_CONDITION
 * @description Default structure for a new person condition.
 */
const DEFAULT_PERSON_CONDITION = {
  field: getFieldList('person', 'conditions')[0] || '',
  operator: OPERATORS[0] || '==',
  value: '',
};

/**
 * @constant DEFAULT_PERSON_CHANGE
 * @description Default structure for a new person change.
 */
const DEFAULT_PERSON_CHANGE = {
  field: getFieldList('person', 'changes')[0] || '',
  value: '',
};

/**
 * @constant DEFAULT_PERSON_RULE
 * @description Default structure for a new person rule.
 */
const DEFAULT_PERSON_RULE = {
  conditions: [],
  changes: [],
};
// --- END: Added Person Defaults ---

// --- BEGIN: Added Incident Defaults ---
/**
 * @constant DEFAULT_INCIDENT_CONDITION
 * @description Default structure for a new incident condition.
 */
const DEFAULT_INCIDENT_CONDITION = {
  field: getFieldList('incident', 'conditions')[0] || '',
  operator: OPERATORS[0] || '==',
  value: '',
};

/**
 * @constant DEFAULT_INCIDENT_CHANGE
 * @description Default structure for a new incident change.
 */
const DEFAULT_INCIDENT_CHANGE = {
  field: getFieldList('incident', 'changes')[0] || '',
  value: '',
};

/**
 * @constant DEFAULT_INCIDENT_RULE
 * @description Default structure for a new incident rule.
 */
const DEFAULT_INCIDENT_RULE = {
  conditions: [],
  changes: [],
};

/**
 * @constant DEFAULT_CHANGE_CONDITION
 * @description Default structure for a new change condition.
 */
const DEFAULT_CHANGE_CONDITION = {
  field: getFieldList('change', 'conditions')[0] || '',
  operator: OPERATORS[0] || '==',
  value: '',
};

/**
 * @constant DEFAULT_CHANGE_CHANGE
 * @description Default structure for a new change change.
 */
const DEFAULT_CHANGE_CHANGE = {
  field: getFieldList('change', 'changes')[0] || '',
  value: '',
};

/**
 * @constant DEFAULT_CHANGE_RULE
 * @description Default structure for a new change rule.
 */
const DEFAULT_CHANGE_RULE = {
  conditions: [],
  changes: [],
};

const DEFAULT_OPERATOR_PROFILE = {
  id: 'operator-profile-1',
  name: 'Operator Profile 1',
  enabled: true,
  minAgeDays: 90,
  defaultArchivingReason: '',
  rules: [],
};

const DEFAULT_PERSON_PROFILE = {
  id: 'person-profile-1',
  name: 'Person Profile 1',
  enabled: true,
  minAgeDays: 90,
  defaultArchivingReason: '',
  rules: [],
};

const DEFAULT_INCIDENT_PROFILE = {
  id: 'incident-profile-1',
  name: 'Incident Profile 1',
  enabled: true,
  dateField: 'modificationDate',
  minAgeDays: 90,
  updateStatuses: [],
  includeArchived: false,
  defaultArchivingReason: '',
  cleanup: {
    deleteAttachments: false,
    deleteActions: false,
    deleteRequests: false,
  },
  rules: [],
};

const DEFAULT_CHANGE_PROFILE = {
  id: 'change-profile-1',
  name: 'Change Profile 1',
  enabled: true,
  dateField: 'modificationDate',
  minAgeDays: 90,
  includeArchived: false,
  defaultArchivingReason: '',
  cleanup: {
    deleteAttachments: false,
    deleteActions: false,
    deleteRequests: false,
  },
  rules: [],
};

/**
 * @constant DEFAULT_SETTINGS
 * @description Default values for EATD settings to ensure form fields are controlled.
 */
const DEFAULT_SETTINGS = {
  // Operator Settings
  operatorAnonymizationEnabled: true,
  inactiveDaysThreshold: 90,
  operatorRules: [],
  operatorDefaultChanges: [],
  operatorProfiles: [],

  // Incident Settings
  incidentProcessingEnabled: false,
  incidentUpdateStatuses: [],
  incidentInactiveDaysThreshold: 90,
  incidentRules: [],
  incidentDefaultChanges: [],
  incidentProfiles: [],
  
  // Archived Incident Settings
  includeArchived: false,
  defaultArchivingReason: '',

  // Person Settings
  personProcessingEnabled: false,
  personRules: [],
  personDefaultChanges: [],
  personInactiveDaysThreshold: 90,
  personProfiles: [],

  // Change Settings
  changeProcessingEnabled: false,
  changeRules: [],
  changeDefaultChanges: [],
  changeProfiles: [],

  // Add previously existing fields
  toDeleteAttachment: false,

  // Note: Global replacement caller feature has been deprecated in favor of rule-based replacement
  // These fields are kept for backward compatibility but shouldn't be used in new code
  useReplacementCaller: false,
  replacementCallerId: '',
};

/**
 * @class EATDOptionsForm
 * @description Form for configuring EATD-specific settings (Operator, Incident, Person).
 */
class EATDOptionsForm extends Component {
  static propTypes = {
    instanceId: PropTypes.string.isRequired,
    instance: PropTypes.object,
    initialValues: PropTypes.object,
    isSaving: PropTypes.bool,
    saveError: PropTypes.object,
    instanceSave: PropTypes.func.isRequired,
    instanceSettingsSave: PropTypes.func.isRequired,
    endpointsByType: PropTypes.object, // Accept endpointsByType for endpointId
    isSetupMode: PropTypes.bool,
  };

  /**
   * @constructor
   * @param {object} props - Component props.
   */
  constructor (props) {
    super(props);

    // Initialize state directly from props here
    const initialInstanceData = _.get(props, 'instance', null);
    const initialSettings = _.defaultsDeep({}, this.getSettingsFromProps(props), DEFAULT_SETTINGS);
    const validatedInitialSettings = EATDOptionsForm.validateAndCleanupSettings(initialSettings);

    // Log initial settings to check if replacement caller settings are loaded correctly
    // console.log("[EATDOptionsForm constructor] Initial settings:", { 
    //   useReplacementCaller: validatedInitialSettings.useReplacementCaller,
    //   replacementCallerId: validatedInitialSettings.replacementCallerId,
    //   rawSettings: _.get(initialInstanceData, 'settings.useReplacementCaller'),
    // });

    /**
     * @property {object} state - Component's local state.
     * @property {object} state.settings - Holds the current form values for EATD settings.
     * @property {object} state.validationErrors - Stores validation errors for rules.
     * @property {string} state.activeTab - Tracks the currently active tab ('operator' or 'incident').
     */
    this.state = {
      settings: validatedInitialSettings, // Initialize with validated settings from props
      validationErrors: {},
      activeTab: getEatdActiveTab(_.get(initialInstanceData, 'id', props.instanceId)),
      topdeskConditionOptions: {},
      topdeskConditionOptionsLoading: false,
      activeProfileIndexes: {
        operator: 0,
        incident: 0,
        person: 0,
        change: 0,
      },
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.toggleTab = this.toggleTab.bind(this);

    // Bind rule handlers
    this.addRule = this.addRule.bind(this);
    this.removeRule = this.removeRule.bind(this);
    this.addCondition = this.addCondition.bind(this);
    this.removeCondition = this.removeCondition.bind(this);
    this.addChange = this.addChange.bind(this);
    this.removeChange = this.removeChange.bind(this);
    this.handleRuleUpdate = this.handleRuleUpdate.bind(this);
    this.handleDefaultChangeUpdate = this.handleDefaultChangeUpdate.bind(this);
    this.addDefaultChange = this.addDefaultChange.bind(this);
    this.removeDefaultChange = this.removeDefaultChange.bind(this);
  }

  getSettingsFromProps = (props = this.props) => {
    const initialValues = _.get(props, 'initialValues', null);
    if (_.isPlainObject(initialValues)) {
      return initialValues;
    }
    return _.get(props, 'instance.settings', {});
  };

  /**
   * @function componentDidMount
   * @description Check initial props when component mounts
   */
  componentDidMount () {
    const storedTab = getEatdActiveTab(this.props.instanceId);
    if (storedTab !== this.state.activeTab) {
      this.setState({ activeTab: storedTab });
    }

    this.loadTopdeskConditionOptions();
  }

  /**
   * @function componentDidUpdate
   * @description Handle updates when props change, especially after saving settings
   * @param {object} prevProps - Previous props
   * @param {object} prevState - Previous state
   */
  componentDidUpdate (prevProps, prevState) {
    const prevInstance = _.get(prevProps, 'instance', null);
    const currentInstance = _.get(this.props, 'instance', null);
    const prevInstanceId = _.get(prevInstance, 'id', prevProps.instanceId);
    const currentInstanceId = _.get(currentInstance, 'id', this.props.instanceId);
    const prevSettings = this.getSettingsFromProps(prevProps);
    const currentSettings = this.getSettingsFromProps(this.props);

    if (!_.isEqual(prevSettings, currentSettings) || prevInstanceId !== currentInstanceId) {
      // Update state with new settings from the latest saved values.
      const newSettings = _.defaultsDeep({}, currentSettings, DEFAULT_SETTINGS);
      const validatedSettings = EATDOptionsForm.validateAndCleanupSettings(newSettings);

      this.setState({
        settings: validatedSettings,
      });
    }

    if (prevProps.instanceId !== this.props.instanceId) {
      this.setState({
        activeTab: getEatdActiveTab(this.props.instanceId),
      });
    }

    if (this.getTopdeskEndpointId(prevProps) !== this.getTopdeskEndpointId(this.props)) {
      this.loadTopdeskConditionOptions();
    }
  }

  getTopdeskEndpointId = (props = this.props) => {
    const topdeskEndpoint = Array.isArray(props.endpointsByType?.topdesk)
      ? props.endpointsByType.topdesk[0]
      : props.endpointsByType?.topdesk;
    return topdeskEndpoint?.id || null;
  };

  loadTopdeskConditionOptions = async () => {
    const endpointId = this.getTopdeskEndpointId();
    if (!endpointId) {
      this.setState({
        topdeskConditionOptions: {},
        topdeskConditionOptionsLoading: false,
      });
      return;
    }

    this.setState({ topdeskConditionOptionsLoading: true });

    try {
      const optionSources = Object.keys(TOPDESK_CONDITION_OPTION_SOURCES);
      const responses = await Promise.all(optionSources.map(async optionSource => {
        const { responseProp } = TOPDESK_CONDITION_OPTION_SOURCES[optionSource];
        const response = await axios.get(`/api/endpoint/topdesk/${endpointId}/searchRemote/${optionSource}`, {
          params: { name: '' },
        });
        const items = _.get(response, `data.${responseProp}`, []);
        return [optionSource, _.isArray(items) ? items.map(item => ({
          id: item.name,
          text: item.name,
          value: item.name,
        })) : []];
      }));

      this.setState({
        topdeskConditionOptions: Object.fromEntries(responses),
        topdeskConditionOptionsLoading: false,
      });
    } catch (error) {
      this.setState({
        topdeskConditionOptions: {},
        topdeskConditionOptionsLoading: false,
      });
    }
  };

  /**
   * @function validateAndCleanupSettings
   * @description Static helper to perform validation and defaulting logic on a settings object.
   *              Moved from getDerivedStateFromProps to be reusable.
   * @param {object} settingsInput - The raw settings object.
   * @returns {object} The validated and cleaned settings object.
   */
  static validateAndCleanupSettings (settingsInput) {
    const cleanedSettings = _.cloneDeep(settingsInput); // Work on a copy

    cleanedSettings.operatorAnonymizationEnabled = !!cleanedSettings.operatorAnonymizationEnabled;
    cleanedSettings.incidentProcessingEnabled = !!cleanedSettings.incidentProcessingEnabled;
    cleanedSettings.toDeleteAttachment = !!cleanedSettings.toDeleteAttachment;
    cleanedSettings.personProcessingEnabled = !!cleanedSettings.personProcessingEnabled;
    cleanedSettings.changeProcessingEnabled = !!cleanedSettings.changeProcessingEnabled;
    
    // Add archived incident settings validation
    cleanedSettings.includeArchived = !!cleanedSettings.includeArchived;
    
    // For backward compatibility: automatically enable useReplacementCaller if replacementCallerId exists
    // This ensures existing installations keep working as expected
    if (cleanedSettings.replacementCallerId && cleanedSettings.replacementCallerId.trim() !== '') {
      cleanedSettings.useReplacementCaller = true;
    } else {
      cleanedSettings.useReplacementCaller = !!cleanedSettings.useReplacementCaller;
    }

    cleanedSettings.inactiveDaysThreshold = _.parseInt(cleanedSettings.inactiveDaysThreshold) || 0;
    if (cleanedSettings.inactiveDaysThreshold < 0) cleanedSettings.inactiveDaysThreshold = 0; // Ensure non-negative
    cleanedSettings.incidentInactiveDaysThreshold = _.parseInt(cleanedSettings.incidentInactiveDaysThreshold) || 0;
    if (cleanedSettings.incidentInactiveDaysThreshold < 0) cleanedSettings.incidentInactiveDaysThreshold = 0; // Ensure non-negative
    cleanedSettings.personInactiveDaysThreshold = _.parseInt(cleanedSettings.personInactiveDaysThreshold) || 0;
    if (cleanedSettings.personInactiveDaysThreshold < 0) cleanedSettings.personInactiveDaysThreshold = 0; // Ensure non-negative

    cleanedSettings.operatorRules = _.isArray(cleanedSettings.operatorRules) ? cleanedSettings.operatorRules : [];
    cleanedSettings.operatorDefaultChanges = _.isArray(cleanedSettings.operatorDefaultChanges) ? cleanedSettings.operatorDefaultChanges : [];
    cleanedSettings.operatorProfiles = _.isArray(cleanedSettings.operatorProfiles) ? cleanedSettings.operatorProfiles : [];
    cleanedSettings.incidentRules = _.isArray(cleanedSettings.incidentRules) ? cleanedSettings.incidentRules : [];
    cleanedSettings.incidentDefaultChanges = _.isArray(cleanedSettings.incidentDefaultChanges) ? cleanedSettings.incidentDefaultChanges : [];
    cleanedSettings.incidentUpdateStatuses = _.isArray(cleanedSettings.incidentUpdateStatuses) ? cleanedSettings.incidentUpdateStatuses : [];
    cleanedSettings.personRules = _.isArray(cleanedSettings.personRules) ? cleanedSettings.personRules : [];
    cleanedSettings.personDefaultChanges = _.isArray(cleanedSettings.personDefaultChanges) ? cleanedSettings.personDefaultChanges : [];
    cleanedSettings.personProfiles = _.isArray(cleanedSettings.personProfiles) ? cleanedSettings.personProfiles : [];
    cleanedSettings.changeRules = _.isArray(cleanedSettings.changeRules) ? cleanedSettings.changeRules : [];
    cleanedSettings.changeDefaultChanges = _.isArray(cleanedSettings.changeDefaultChanges) ? cleanedSettings.changeDefaultChanges : [];
    cleanedSettings.incidentProfiles = _.isArray(cleanedSettings.incidentProfiles) ? cleanedSettings.incidentProfiles : [];
    cleanedSettings.changeProfiles = _.isArray(cleanedSettings.changeProfiles) ? cleanedSettings.changeProfiles : [];

    cleanedSettings.incidentUpdateFiqlQuery = _.isString(cleanedSettings.incidentUpdateFiqlQuery) ? cleanedSettings.incidentUpdateFiqlQuery : '';
    cleanedSettings.replacementCallerId = _.isString(cleanedSettings.replacementCallerId) ? cleanedSettings.replacementCallerId : '';
    cleanedSettings.defaultArchivingReason = _.isString(cleanedSettings.defaultArchivingReason) ? cleanedSettings.defaultArchivingReason : '';

    const cleanRule = (ruleType, rule, defaultCondition, defaultChange) => {
      const conditionFieldList = getFieldList(ruleType, 'conditions');
      const changeFieldList = getFieldList(ruleType, 'changes');
      rule.conditions = (_.isArray(rule.conditions) ? rule.conditions : [])
        .map(cond => {
          const nextCondition = { ...defaultCondition, ...cond };
          const fieldDefinition = getFieldDefinition(ruleType, 'conditions', nextCondition.field);
          nextCondition.value = normalizeValueForField(fieldDefinition, nextCondition.value);
          return nextCondition;
        })
        .filter(cond => cond.field && cond.operator && conditionFieldList.includes(cond.field));
      rule.changes = (_.isArray(rule.changes) ? rule.changes : [])
        .map(change => ({ ...defaultChange, ...change }))
        .filter(change => change.field && changeFieldList.includes(change.field));
      return rule;
    };

    cleanedSettings.operatorRules = cleanedSettings.operatorRules
      .map(rule => cleanRule('operator', rule, DEFAULT_OPERATOR_CONDITION, DEFAULT_OPERATOR_CHANGE))
      .filter(rule => rule.changes.length > 0); // Empty conditions mean "always apply"

    cleanedSettings.incidentRules = cleanedSettings.incidentRules
      .map(rule => cleanRule('incident', rule, DEFAULT_INCIDENT_CONDITION, DEFAULT_INCIDENT_CHANGE))
      .filter(rule => rule.changes.length > 0);

    cleanedSettings.personRules = cleanedSettings.personRules
      .map(rule => cleanRule('person', rule, DEFAULT_PERSON_CONDITION, DEFAULT_PERSON_CHANGE))
      .filter(rule => rule.changes.length > 0);

    cleanedSettings.changeRules = cleanedSettings.changeRules
      .map(rule => cleanRule('change', rule, DEFAULT_CHANGE_CONDITION, DEFAULT_CHANGE_CHANGE))
      .filter(rule => rule.conditions.length > 0 && rule.changes.length > 0);


    // Clean up default changes
    const cleanDefaultChange = (change, defaultChange) => ({
      ...defaultChange,
      ...change,
    });
    cleanedSettings.operatorDefaultChanges = cleanedSettings.operatorDefaultChanges
      .map(change => cleanDefaultChange(change, DEFAULT_OPERATOR_CHANGE))
      .filter(change => change.field && getFieldList('operator', 'changes').includes(change.field));

    cleanedSettings.incidentDefaultChanges = cleanedSettings.incidentDefaultChanges
      .map(change => cleanDefaultChange(change, DEFAULT_INCIDENT_CHANGE))
      .filter(change => change.field && getFieldList('incident', 'changes').includes(change.field));

    cleanedSettings.personDefaultChanges = cleanedSettings.personDefaultChanges
      .map(change => cleanDefaultChange(change, DEFAULT_PERSON_CHANGE))
      .filter(change => change.field && getFieldList('person', 'changes').includes(change.field));

    cleanedSettings.changeDefaultChanges = cleanedSettings.changeDefaultChanges
      .map(change => cleanDefaultChange(change, DEFAULT_CHANGE_CHANGE))
      .filter(change => change.field && getFieldList('change', 'changes').includes(change.field));

    const normalizeProfileCleanup = cleanup => ({
      deleteAttachments: !!_.get(cleanup, 'deleteAttachments', false),
      deleteActions: !!_.get(cleanup, 'deleteActions', false),
      deleteRequests: !!_.get(cleanup, 'deleteRequests', false),
    });

    const buildProfileRules = (ruleType, rules, defaultChanges, defaultCondition, defaultChange) => {
      const cleanedRules = (rules || [])
        .map(rule => cleanRule(ruleType, rule, defaultCondition, defaultChange))
        .filter(rule => rule.changes.length > 0);

      const migratedDefaultRule = (defaultChanges || []).length > 0 ? [{
        conditions: [],
        changes: (defaultChanges || [])
          .map(change => cleanDefaultChange(change, defaultChange))
          .filter(change => change.field && getFieldList(ruleType, 'changes').includes(change.field)),
      }] : [];

      return [...cleanedRules, ...migratedDefaultRule.filter(rule => rule.changes.length > 0)];
    };

    cleanedSettings.operatorProfiles = (cleanedSettings.operatorProfiles.length > 0
      ? cleanedSettings.operatorProfiles
      : [{
        ...DEFAULT_OPERATOR_PROFILE,
        minAgeDays: cleanedSettings.inactiveDaysThreshold || DEFAULT_OPERATOR_PROFILE.minAgeDays,
        defaultArchivingReason: cleanedSettings.defaultArchivingReason,
        rules: buildProfileRules(
          'operator',
          cleanedSettings.operatorRules,
          cleanedSettings.operatorDefaultChanges,
          DEFAULT_OPERATOR_CONDITION,
          DEFAULT_OPERATOR_CHANGE,
        ),
      }])
      .map((profile, index) => ({
        ..._.cloneDeep(DEFAULT_OPERATOR_PROFILE),
        ...profile,
        id: profile.id || `operator-profile-${index + 1}`,
        name: profile.name || `Operator Profile ${index + 1}`,
        minAgeDays: _.parseInt(profile.minAgeDays) || 0,
        defaultArchivingReason: _.isString(profile.defaultArchivingReason) ? profile.defaultArchivingReason : '',
        rules: buildProfileRules('operator', profile.rules, [], DEFAULT_OPERATOR_CONDITION, DEFAULT_OPERATOR_CHANGE),
      }));

    cleanedSettings.incidentProfiles = (cleanedSettings.incidentProfiles.length > 0
      ? cleanedSettings.incidentProfiles
      : [{
        ...DEFAULT_INCIDENT_PROFILE,
        minAgeDays: cleanedSettings.incidentInactiveDaysThreshold || DEFAULT_INCIDENT_PROFILE.minAgeDays,
        updateStatuses: cleanedSettings.incidentUpdateStatuses,
        includeArchived: cleanedSettings.includeArchived,
        defaultArchivingReason: cleanedSettings.defaultArchivingReason,
        cleanup: {
          deleteAttachments: cleanedSettings.toDeleteAttachment,
          deleteActions: false,
          deleteRequests: false,
        },
        rules: buildProfileRules(
          'incident',
          cleanedSettings.incidentRules,
          cleanedSettings.incidentDefaultChanges,
          DEFAULT_INCIDENT_CONDITION,
          DEFAULT_INCIDENT_CHANGE,
        ),
      }])
      .map((profile, index) => ({
        ..._.cloneDeep(DEFAULT_INCIDENT_PROFILE),
        ...profile,
        id: profile.id || `incident-profile-${index + 1}`,
        name: profile.name || `Incident Profile ${index + 1}`,
        minAgeDays: _.parseInt(profile.minAgeDays) || 0,
        updateStatuses: _.isArray(profile.updateStatuses) ? profile.updateStatuses : [],
        includeArchived: !!profile.includeArchived,
        defaultArchivingReason: _.isString(profile.defaultArchivingReason) ? profile.defaultArchivingReason : '',
        cleanup: normalizeProfileCleanup(profile.cleanup),
        rules: buildProfileRules('incident', profile.rules, [], DEFAULT_INCIDENT_CONDITION, DEFAULT_INCIDENT_CHANGE),
      }));

    cleanedSettings.changeProfiles = (cleanedSettings.changeProfiles.length > 0
      ? cleanedSettings.changeProfiles
      : [{
        ...DEFAULT_CHANGE_PROFILE,
        rules: buildProfileRules(
          'change',
          cleanedSettings.changeRules,
          cleanedSettings.changeDefaultChanges,
          DEFAULT_CHANGE_CONDITION,
          DEFAULT_CHANGE_CHANGE,
        ),
        cleanup: {
          deleteAttachments: cleanedSettings.toDeleteAttachment,
          deleteActions: false,
          deleteRequests: false,
        },
      }])
      .map((profile, index) => ({
        ..._.cloneDeep(DEFAULT_CHANGE_PROFILE),
        ...profile,
        id: profile.id || `change-profile-${index + 1}`,
        name: profile.name || `Change Profile ${index + 1}`,
        minAgeDays: _.parseInt(profile.minAgeDays) || 0,
        includeArchived: !!profile.includeArchived,
        defaultArchivingReason: _.isString(profile.defaultArchivingReason) ? profile.defaultArchivingReason : '',
        cleanup: normalizeProfileCleanup(profile.cleanup),
        rules: buildProfileRules('change', profile.rules, [], DEFAULT_CHANGE_CONDITION, DEFAULT_CHANGE_CHANGE),
      }));

    cleanedSettings.personProfiles = (cleanedSettings.personProfiles.length > 0
      ? cleanedSettings.personProfiles
      : [{
        ...DEFAULT_PERSON_PROFILE,
        minAgeDays: cleanedSettings.personInactiveDaysThreshold || DEFAULT_PERSON_PROFILE.minAgeDays,
        defaultArchivingReason: cleanedSettings.defaultArchivingReason,
        rules: buildProfileRules(
          'person',
          cleanedSettings.personRules,
          cleanedSettings.personDefaultChanges,
          DEFAULT_PERSON_CONDITION,
          DEFAULT_PERSON_CHANGE,
        ),
      }])
      .map((profile, index) => ({
        ..._.cloneDeep(DEFAULT_PERSON_PROFILE),
        ...profile,
        id: profile.id || `person-profile-${index + 1}`,
        name: profile.name || `Person Profile ${index + 1}`,
        minAgeDays: _.parseInt(profile.minAgeDays) || 0,
        defaultArchivingReason: _.isString(profile.defaultArchivingReason) ? profile.defaultArchivingReason : '',
        rules: buildProfileRules('person', profile.rules, [], DEFAULT_PERSON_CONDITION, DEFAULT_PERSON_CHANGE),
      }));
     
    // Ensure caller field values use the correct format (id:{callerId})
    // Fix global replacement caller
    if (cleanedSettings.replacementCallerId && 
        typeof cleanedSettings.replacementCallerId === 'string' &&
        !cleanedSettings.replacementCallerId.startsWith('id:{') && 
        cleanedSettings.replacementCallerId.trim() !== '') {
      cleanedSettings.replacementCallerId = `id:{${cleanedSettings.replacementCallerId}}`;
      // console.log("[EATDOptionsForm] Fixed global caller format during load:", cleanedSettings.replacementCallerId);
    }
    
    // Fix caller fields in incident rules
    if (cleanedSettings.incidentRules && Array.isArray(cleanedSettings.incidentRules)) {
      cleanedSettings.incidentRules.forEach(rule => {
        if (rule && typeof rule === 'object' && rule.changes && Array.isArray(rule.changes)) {
          rule.changes.forEach(change => {
            if (change && typeof change === 'object' &&
                change.field === 'caller' && 
                change.value && 
                typeof change.value === 'string' && 
                !change.value.startsWith('id:{') && 
                change.value.trim() !== '') {
              change.value = `id:{${change.value}}`;
              // console.log("[EATDOptionsForm] Fixed caller format in rule during load:", change.value);
            }
          });
        }
      });
    }
    
    // Fix caller fields in default changes
    if (cleanedSettings.incidentDefaultChanges && Array.isArray(cleanedSettings.incidentDefaultChanges)) {
      cleanedSettings.incidentDefaultChanges.forEach(change => {
        if (change && typeof change === 'object' && 
            change.field === 'caller' && 
            change.value && 
            typeof change.value === 'string' && 
            !change.value.startsWith('id:{') && 
            change.value.trim() !== '') {
          change.value = `id:{${change.value}}`;
          // console.log("[EATDOptionsForm] Fixed caller format in default change during load:", change.value);
        }
      });
    }

    const changeIdFields = ['requester', 'operator', 'operatorGroup'];
    if (cleanedSettings.changeRules && Array.isArray(cleanedSettings.changeRules)) {
      cleanedSettings.changeRules.forEach(rule => {
        if (rule && typeof rule === 'object' && Array.isArray(rule.changes)) {
          rule.changes.forEach(change => {
            if (change && typeof change === 'object' &&
                changeIdFields.includes(change.field) &&
                typeof change.value === 'string' &&
                change.value.trim() !== '' &&
                !change.value.startsWith('id:{')) {
              change.value = `id:{${change.value}}`;
            }
          });
        }
      });
    }

    if (cleanedSettings.changeDefaultChanges && Array.isArray(cleanedSettings.changeDefaultChanges)) {
      cleanedSettings.changeDefaultChanges.forEach(change => {
        if (change && typeof change === 'object' &&
            changeIdFields.includes(change.field) &&
            typeof change.value === 'string' &&
            change.value.trim() !== '' &&
            !change.value.startsWith('id:{')) {
          change.value = `id:{${change.value}}`;
        }
      });
    }

    cleanedSettings.incidentProfiles.forEach(profile => {
      (profile.rules || []).forEach(rule => {
        (rule.changes || []).forEach(change => {
          if (change.field === 'caller' && typeof change.value === 'string' && change.value.trim() !== '' && !change.value.startsWith('id:{')) {
            change.value = `id:{${change.value}}`;
          }
        });
      });
    });

    cleanedSettings.changeProfiles.forEach(profile => {
      (profile.rules || []).forEach(rule => {
        (rule.changes || []).forEach(change => {
          if (changeIdFields.includes(change.field) && typeof change.value === 'string' && change.value.trim() !== '' && !change.value.startsWith('id:{')) {
            change.value = `id:{${change.value}}`;
          }
        });
      });
    });

    return cleanedSettings;
  }

  /**
   * @function handleChange
   * @description Updates the corresponding value in the local state's settings object.
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>} event - The input change event.
   */
  handleChange (event) {
    const { name, value, type, checked } = event.target;

    // Special handling for multi-select (assuming react-select or similar structure)
    if (name === 'incidentUpdateStatuses' && Array.isArray(value)) {
      this.setState(prevState => ({
        settings: {
          ...prevState.settings,
          incidentUpdateStatuses: value.map(option => option.value), // Extract values if needed
        },
      }));
      return; // Exit early
    }


    // Handle standard inputs
    const parsedValue = type === 'checkbox' ? checked :
      type === 'number' ? (value === '' ? null : parseInt(value, 10)) : // Use null for empty number, parse as int
        value;

    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        [name]: parsedValue,
      },
      // Optionally clear validation errors for the changed field
      // validationErrors: _.omit(prevState.validationErrors, name)
    }));
  }

  /**
   * @function handleSelectChange
   * @description Handles value changes from Select2Wrap components.
   * @param {string} name - The name of the setting field.
   * @param {Array|string|null} selectedValue - The selected value(s).
   *        For multi-select (like incidentUpdateStatuses), this will be an array of values.
   *        For single-select, this will be a single value.
   */
  handleSelectChange = (name, selectedValue) => {
    let valueToSet;
    if (name === 'incidentUpdateStatuses') {
      // For the multi-select statuses field, the incoming `selectedValue`
      // is already the array of values we want to store.
      // Ensure it's an array, default to empty array if not (e.g., if cleared).
      valueToSet = Array.isArray(selectedValue) ? selectedValue : [];
    } else {
      // For single select fields, just use the value directly.
      valueToSet = selectedValue;
    }

    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        [name]: valueToSet,
      },
    }));
  };


  /**
   * @function toggleTab
   * @description Switches the active tab.
   * @param {string} tab - The identifier of the tab to activate ('operator' or 'incident').
   */
  toggleTab (tab) {
    if (this.state.activeTab !== tab) {
      setEatdActiveTab(this.props.instanceId, tab);
      this.setState({
        activeTab: tab,
        validationErrors: {}, // Clear validation errors when switching tabs
      });
    }
  }

  getRulesKey = ruleType => `${ruleType}Rules`;

  getProfilesKey = ruleType => `${ruleType}Profiles`;

  usesProfiles = ruleType => ['operator', 'incident', 'person', 'change'].includes(ruleType);

  getConditionFieldDefinitions = ruleType => getFieldDefinitions(ruleType, 'conditions');

  getChangeFieldDefinitions = ruleType => getFieldDefinitions(ruleType, 'changes');

  getConditionFieldList = ruleType => getFieldList(ruleType, 'conditions');

  getChangeFieldList = ruleType => getFieldList(ruleType, 'changes');

  getConditionFieldDefinition = (ruleType, field) => getFieldDefinition(ruleType, 'conditions', field);

  getChangeFieldDefinition = (ruleType, field) => getFieldDefinition(ruleType, 'changes', field);

  getConditionFieldOptions = ruleType => getFieldSelectData(ruleType, 'conditions');

  getChangeFieldOptions = ruleType => getFieldSelectData(ruleType, 'changes');

  getConditionOperators = (ruleType, field) => getOperatorsForFieldDefinition(this.getConditionFieldDefinition(ruleType, field));

  getConditionValueOptions = (fieldDefinition) => {
    if (!fieldDefinition) return [];
    if (fieldDefinition.inputType === 'enum') {
      return fieldDefinition.options || [];
    }
    if (fieldDefinition.inputType === 'topdeskSelect') {
      return this.state.topdeskConditionOptions[fieldDefinition.topdeskOptionSource] || [];
    }
    return [];
  };

  getDefaultRule = ruleType => (ruleType === 'operator' ? _.cloneDeep(DEFAULT_OPERATOR_RULE) :
    ruleType === 'incident' ? _.cloneDeep(DEFAULT_INCIDENT_RULE) :
      ruleType === 'person' ? _.cloneDeep(DEFAULT_PERSON_RULE) :
        ruleType === 'change' ? _.cloneDeep(DEFAULT_CHANGE_RULE) : {});

  getDefaultCondition = ruleType => (ruleType === 'operator' ? _.cloneDeep(DEFAULT_OPERATOR_CONDITION) :
    ruleType === 'incident' ? _.cloneDeep(DEFAULT_INCIDENT_CONDITION) :
      ruleType === 'person' ? _.cloneDeep(DEFAULT_PERSON_CONDITION) :
        ruleType === 'change' ? _.cloneDeep(DEFAULT_CHANGE_CONDITION) : {});

  getDefaultChange = ruleType => (ruleType === 'operator' ? _.cloneDeep(DEFAULT_OPERATOR_CHANGE) :
    ruleType === 'incident' ? _.cloneDeep(DEFAULT_INCIDENT_CHANGE) :
      ruleType === 'person' ? _.cloneDeep(DEFAULT_PERSON_CHANGE) :
        ruleType === 'change' ? _.cloneDeep(DEFAULT_CHANGE_CHANGE) : {});

  getDefaultProfile = ruleType => (ruleType === 'operator' ? _.cloneDeep(DEFAULT_OPERATOR_PROFILE) :
    ruleType === 'incident' ? _.cloneDeep(DEFAULT_INCIDENT_PROFILE) :
      ruleType === 'person' ? _.cloneDeep(DEFAULT_PERSON_PROFILE) :
        ruleType === 'change' ? _.cloneDeep(DEFAULT_CHANGE_PROFILE) : {});

  getRulesForType = ruleType => {
    return this.getRulesForTypeFromSettings(this.state.settings, ruleType, this.state.activeProfileIndexes);
  };

  getRulesForTypeFromSettings = (settings, ruleType, activeProfileIndexes = { incident: 0, change: 0 }) => {
    if (!this.usesProfiles(ruleType)) {
      return settings[this.getRulesKey(ruleType)] || [];
    }

    const profiles = settings[this.getProfilesKey(ruleType)] || [];
    const activeIndex = activeProfileIndexes[ruleType] || 0;
    return (profiles[activeIndex] && profiles[activeIndex].rules) || [];
  };

  updateRulesForType = (prevSettings, ruleType, updatedRules) => {
    if (!this.usesProfiles(ruleType)) {
      return {
        ...prevSettings,
        [this.getRulesKey(ruleType)]: updatedRules,
      };
    }

    const profilesKey = this.getProfilesKey(ruleType);
    const profiles = _.cloneDeep(prevSettings[profilesKey] || []);
    const activeIndex = _.get(this.state, `activeProfileIndexes.${ruleType}`, 0);
    if (!profiles[activeIndex]) {
      profiles[activeIndex] = this.getDefaultProfile(ruleType);
    }
    profiles[activeIndex].rules = updatedRules;

    return {
      ...prevSettings,
      [profilesKey]: profiles,
    };
  };

  updateProfileField = (ruleType, fieldPath, value) => {
    const profilesKey = this.getProfilesKey(ruleType);
    this.setState(prevState => {
      const profiles = _.cloneDeep(prevState.settings[profilesKey] || []);
      const activeIndex = _.get(prevState, `activeProfileIndexes.${ruleType}`, 0);
      if (!profiles[activeIndex]) {
        profiles[activeIndex] = this.getDefaultProfile(ruleType);
      }
      _.set(profiles[activeIndex], fieldPath, value);
      return {
        settings: {
          ...prevState.settings,
          [profilesKey]: profiles,
        },
      };
    });
  };

  addProfile = ruleType => {
    const profilesKey = this.getProfilesKey(ruleType);
    this.setState(prevState => {
      const profiles = _.cloneDeep(prevState.settings[profilesKey] || []);
      const nextIndex = profiles.length;
      const nextProfile = this.getDefaultProfile(ruleType);
      nextProfile.id = `${ruleType}-profile-${nextIndex + 1}`;
      nextProfile.name = `${_.capitalize(ruleType)} Profile ${nextIndex + 1}`;
      profiles.push(nextProfile);
      return {
        settings: {
          ...prevState.settings,
          [profilesKey]: profiles,
        },
        activeProfileIndexes: {
          ...prevState.activeProfileIndexes,
          [ruleType]: nextIndex,
        },
      };
    });
  };

  removeProfile = (ruleType, index) => {
    const profilesKey = this.getProfilesKey(ruleType);
    this.setState(prevState => {
      const profiles = _.cloneDeep(prevState.settings[profilesKey] || []);
      profiles.splice(index, 1);
      return {
        settings: {
          ...prevState.settings,
          [profilesKey]: profiles,
        },
        activeProfileIndexes: {
          ...prevState.activeProfileIndexes,
          [ruleType]: Math.max(0, Math.min(index - 1, profiles.length - 1)),
        },
      };
    });
  };

  selectProfile = (ruleType, index) => {
    this.setState(prevState => ({
      activeProfileIndexes: {
        ...prevState.activeProfileIndexes,
        [ruleType]: index,
      },
    }));
  };


  /**
   * @function validateRules
   * @description Validates the rules array for the currently active tab and replacement caller logic.
   * @param {string} ruleType - 'operator' or 'incident'.
   * @returns {object} An object containing validation errors.
   */
  validateRules (ruleType) {
    const errors = {};
    const rules = this.getRulesForType(ruleType);
    const conditionFieldDefinitions = this.getConditionFieldDefinitions(ruleType);
    const changeFieldDefinitions = this.getChangeFieldDefinitions(ruleType);

    if (!Array.isArray(rules)) {
      return { _error: `${_.capitalize(ruleType)} Rules data is corrupted.` };
    }

    rules.forEach((rule, ruleIndex) => {
      const ruleErrors = {};

      // Validate conditions
      if (rule.conditions && Array.isArray(rule.conditions)) {
        rule.conditions.forEach((condition, conditionIndex) => {
          const conditionErrors = this.validateCondition(condition, conditionFieldDefinitions);
          if (Object.keys(conditionErrors).length > 0) {
            if (!ruleErrors.conditions) ruleErrors.conditions = {};
            ruleErrors.conditions[conditionIndex] = conditionErrors;
          }
        });
      } else if (rule.conditions) {
        ruleErrors.conditions = { _error: 'Conditions must be an array.' };
      }

      // Validate changes
      if (rule.changes && Array.isArray(rule.changes)) {
        if (rule.changes.length === 0 && (!rule.conditions || rule.conditions.length === 0)) {
          // Warn if rule has no conditions and no changes (ineffective rule)
          // ruleErrors._warning = "Rule has no conditions and no changes.";
        }
        rule.changes.forEach((change, changeIndex) => {
          const changeErrors = this.validateChange(change, changeFieldDefinitions);
          if (Object.keys(changeErrors).length > 0) {
            if (!ruleErrors.changes) ruleErrors.changes = {};
            ruleErrors.changes[changeIndex] = changeErrors;
          }
        });
      } else if (rule.changes) {
        ruleErrors.changes = { _error: 'Changes must be an array.' };
      }

      if (Object.keys(ruleErrors).length > 0) {
        errors[ruleIndex] = ruleErrors;
      }
    });

    return errors;
  }

  /**
   * @function validateReplacementCaller
   * @description Validates if the global replacement caller is properly configured.
   * @returns {object} Validation errors for replacement caller.
   * @deprecated Use rule-based or default change caller replacement instead
   */
  validateReplacementCaller () {
    // For backward compatibility, we don't show any validation errors
    // The automatic enabling in validateAndCleanupSettings handles this now
    return {};
  }

  /**
   * @function validateDefaultChanges
   * @description Validates the default changes array.
   * @param {string} changeType - 'operator' or 'incident'.
   * @returns {object} An object containing validation errors.
   */
  validateDefaultChanges (changeType) {
    const errors = {};
    const changes = this.state.settings[`${changeType}DefaultChanges`] || [];
    const fieldDefinitions = this.getChangeFieldDefinitions(changeType);

    if (!Array.isArray(changes)) {
      return { _error: `${_.capitalize(changeType)} Default Changes data is corrupted.` };
    }

    changes.forEach((change, index) => {
      const changeErrors = this.validateChange(change, fieldDefinitions);
      if (Object.keys(changeErrors).length > 0) {
        errors[index] = changeErrors;
      }
    });

    return errors;
  }


  /**
   * @function validateCondition
   * @description Validates a single condition object.
   * @param {object} condition - The condition object.
   * @param {Array<object>} fieldDefinitions - The list of valid field definitions for this condition type.
   * @returns {object} Validation errors for the condition.
   */
  validateCondition (condition, fieldDefinitions) {
    const errors = {};
    const fieldDefinition = fieldDefinitions.find(definition => definition.field === condition.field);
    if (!condition.field || !fieldDefinition) {
      errors.field = 'Invalid or missing field selected.';
    }
    const allowedOperators = getOperatorsForFieldDefinition(fieldDefinition);
    if (!condition.operator || !allowedOperators.includes(condition.operator)) {
      errors.operator = 'Invalid or missing operator.';
    }

    if (
      (fieldDefinition?.inputType === 'enum' || fieldDefinition?.inputType === 'topdeskSelect') &&
      condition.value !== '' &&
      condition.value !== undefined &&
      condition.value !== null
    ) {
      const allowedValues = this.getConditionValueOptions(fieldDefinition).map(option => option.value);
      if (!allowedValues.includes(condition.value)) {
        errors.value = 'Select a valid value.';
      }
    }

    if (fieldDefinition?.inputType === 'number' && condition.value !== '' && condition.value !== undefined && condition.value !== null) {
      if (!Number.isFinite(condition.value)) {
        errors.value = 'Value must be a number.';
      }
    }

    return errors;
  }

  /**
   * @function validateChange
   * @description Validates a single change object.
   * @param {object} change - The change object.
   * @param {Array<object>} fieldDefinitions - The list of valid field definitions for this change type.
   * @returns {object} Validation errors for the change.
   */
  validateChange (change, fieldDefinitions) {
    const errors = {};
    const fieldDefinition = fieldDefinitions.find(definition => definition.field === change.field);
    if (!change.field || !fieldDefinition) {
      errors.field = 'Invalid or missing field selected.';
    }
    return errors;
  }

  /**
   * @function handleSave
   * @description Validates the current tab's rules/changes and saves the entire settings object if valid.
   * @param {React.FormEvent<HTMLFormElement>} event - The form submission event.
   */
  handleSave (event) {
    event.preventDefault(); // Prevent default form submission

    const { instanceId, instanceSave, instanceSettingsSave, isSetupMode } = this.props;
    const { activeTab, settings } = this.state;

    // DEBUGGING: Log the full settings before saving
    // console.log("[EATDOptionsForm handleSave] PRE-SAVE SETTINGS:", JSON.stringify(settings, null, 2));

    // Validate the rules and default changes for the *active* tab
    const ruleErrors = this.validateRules(activeTab);
    const defaultChangeErrors = {};
    
    // Add validation for archived incident/operator/person/change settings
    const archivedIncidentErrors = {};
    const activeIncidentProfileIndex = _.get(this.state, 'activeProfileIndexes.incident', 0);
    const activeIncidentProfile = _.get(settings, `incidentProfiles[${activeIncidentProfileIndex}]`, null);
    if (activeTab === 'incident' && activeIncidentProfile?.includeArchived && !activeIncidentProfile?.defaultArchivingReason) {
      archivedIncidentErrors.defaultArchivingReason = 'Default archiving reason is required when \'Include Archived Incidents\' is enabled.';
    }
    const activeOperatorProfileIndex = _.get(this.state, 'activeProfileIndexes.operator', 0);
    const activeOperatorProfile = _.get(settings, `operatorProfiles[${activeOperatorProfileIndex}]`, null);
    if (activeTab === 'operator' && !activeOperatorProfile?.defaultArchivingReason) {
      archivedIncidentErrors.defaultArchivingReason = 'Default archiving reason is required for archived operator anonymization.';
    }
    const activePersonProfileIndex = _.get(this.state, 'activeProfileIndexes.person', 0);
    const activePersonProfile = _.get(settings, `personProfiles[${activePersonProfileIndex}]`, null);
    if (activeTab === 'person' && !activePersonProfile?.defaultArchivingReason) {
      archivedIncidentErrors.defaultArchivingReason = 'Default archiving reason is required for archived person anonymization.';
    }
    const activeChangeProfileIndex = _.get(this.state, 'activeProfileIndexes.change', 0);
    const activeChangeProfile = _.get(settings, `changeProfiles[${activeChangeProfileIndex}]`, null);
    if (activeTab === 'change' && activeChangeProfile?.includeArchived && !activeChangeProfile?.defaultArchivingReason) {
      archivedIncidentErrors.defaultArchivingReason = 'Default archiving reason is required when archived changes are included.';
    }
    
    const combinedErrors = {};
    if (Object.keys(ruleErrors).length > 0) {
      combinedErrors[`${activeTab}Rules`] = ruleErrors;
    }
    if (Object.keys(defaultChangeErrors).length > 0) {
      combinedErrors[`${activeTab}DefaultChanges`] = defaultChangeErrors;
    }
    if (Object.keys(archivedIncidentErrors).length > 0) {
      combinedErrors.archivedIncident = archivedIncidentErrors;
    }

    if (Object.keys(combinedErrors).length > 0) {
      this.setState({ validationErrors: combinedErrors });
      // Optionally show a general validation error message to the user
      alert(`Please fix the errors in the ${_.capitalize(activeTab)} settings before saving.`);
      return; // Stop saving
    }

    // Clear validation errors if successful
    this.setState({ validationErrors: {} });

    // Create a fresh copy of settings
    const settingsToSave = _.cloneDeep(settings);
    
    // Ensure backward compatibility: If replacementCallerId exists, enable the feature
    if (settingsToSave.replacementCallerId && settingsToSave.replacementCallerId.trim() !== '') {
      settingsToSave.useReplacementCaller = true;
    }
    
    // Ensure all caller field values are properly formatted before saving
    this.ensureCallerFormats(settingsToSave);
    
    // console.log("[EATDOptionsForm handleSave] FINAL SETTINGS TO SAVE:", JSON.stringify(settingsToSave, null, 2));
    
    // Log the exact function and parameters we're using
    // console.log("[EATDOptionsForm handleSave] CALLING instanceSave with:", {
    //   instanceId,
    //   settingsObj: { settings: settingsToSave }
    // });

    // Save the settings
    if (isSetupMode) {
      instanceSave(instanceId, { settings: settingsToSave }, INSTANCE_SETTINGS_UPDATE);
      return;
    }

    instanceSettingsSave('eatd', instanceId, settingsToSave);
  }

  /**
   * @function ensureCallerFormats
   * @description Ensures all caller field values are properly formatted before saving
   * @param {Object} settingsObj - The settings object to process
   */
  ensureCallerFormats = (settingsObj) => {
    // Process incident rules
    if (settingsObj.incidentRules && Array.isArray(settingsObj.incidentRules)) {
      settingsObj.incidentRules.forEach(rule => {
        if (rule && typeof rule === 'object' && rule.changes && Array.isArray(rule.changes)) {
          rule.changes.forEach(change => {
            // If this is a caller field and the value doesn't have the proper format
            if (change && typeof change === 'object' &&
                change.field === 'caller' && 
                change.value && 
                typeof change.value === 'string' && 
                !change.value.startsWith('id:{')) {
              // console.log(`[EATDOptionsForm] Fixing caller format in rule: ${change.value} -> id:{${change.value}}`);
              change.value = this.formatCallerValue(change.value);
            }
          });
        }
      });
    }
    
    // Process incident default changes
    if (settingsObj.incidentDefaultChanges && Array.isArray(settingsObj.incidentDefaultChanges)) {
      settingsObj.incidentDefaultChanges.forEach(change => {
        // If this is a caller field and the value doesn't have the proper format
        if (change && typeof change === 'object' &&
            change.field === 'caller' && 
            change.value && 
            typeof change.value === 'string' && 
            !change.value.startsWith('id:{')) {
          // console.log(`[EATDOptionsForm] Fixing caller format in default change: ${change.value} -> id:{${change.value}}`);
          change.value = this.formatCallerValue(change.value);
        }
      });
    }
    
    // Also check the global replacement caller (for backward compatibility)
    if (settingsObj.replacementCallerId && 
        typeof settingsObj.replacementCallerId === 'string' && 
        !settingsObj.replacementCallerId.startsWith('id:{')) {
      // console.log(`[EATDOptionsForm] Fixing global caller format: ${settingsObj.replacementCallerId} -> id:{${settingsObj.replacementCallerId}}`);
      settingsObj.replacementCallerId = this.formatCallerValue(settingsObj.replacementCallerId);
    }

    const changeIdFields = ['requester', 'operator', 'operatorGroup'];
    if (settingsObj.changeRules && Array.isArray(settingsObj.changeRules)) {
      settingsObj.changeRules.forEach(rule => {
        if (rule && typeof rule === 'object' && Array.isArray(rule.changes)) {
          rule.changes.forEach(change => {
            if (change && typeof change === 'object' &&
                changeIdFields.includes(change.field) &&
                typeof change.value === 'string' &&
                change.value.trim() !== '' &&
                !change.value.startsWith('id:{')) {
              change.value = this.formatCallerValue(change.value);
            }
          });
        }
      });
    }

    if (settingsObj.changeDefaultChanges && Array.isArray(settingsObj.changeDefaultChanges)) {
      settingsObj.changeDefaultChanges.forEach(change => {
        if (change && typeof change === 'object' &&
            changeIdFields.includes(change.field) &&
            typeof change.value === 'string' &&
            change.value.trim() !== '' &&
            !change.value.startsWith('id:{')) {
          change.value = this.formatCallerValue(change.value);
        }
      });
    }

    if (settingsObj.incidentProfiles && Array.isArray(settingsObj.incidentProfiles)) {
      settingsObj.incidentProfiles.forEach(profile => {
        (profile.rules || []).forEach(rule => {
          (rule.changes || []).forEach(change => {
            if (change && change.field === 'caller' && typeof change.value === 'string' && !change.value.startsWith('id:{')) {
              change.value = this.formatCallerValue(change.value);
            }
          });
        });
      });
    }

    if (settingsObj.changeProfiles && Array.isArray(settingsObj.changeProfiles)) {
      settingsObj.changeProfiles.forEach(profile => {
        (profile.rules || []).forEach(rule => {
          (rule.changes || []).forEach(change => {
            if (change && changeIdFields.includes(change.field) && typeof change.value === 'string' && change.value.trim() !== '' && !change.value.startsWith('id:{')) {
              change.value = this.formatCallerValue(change.value);
            }
          });
        });
      });
    }
    
    return settingsObj;
  };


  /**
   * @function addRule
   * @description Adds a new default rule to the specified rule type ('operator' or 'incident').
   * @param {string} ruleType - 'operator' or 'incident'.
   */
  addRule (ruleType) {
    const newRule = this.getDefaultRule(ruleType);
    const ruleKey = this.getRulesKey(ruleType);

    this.setState(prevState => ({
      settings: this.updateRulesForType(prevState.settings, ruleType, [...this.getRulesForTypeFromSettings(prevState.settings, ruleType, prevState.activeProfileIndexes), newRule]),
      // Clear validation errors for the specific rule set being modified
      validationErrors: _.omit(prevState.validationErrors, ruleKey),
    }));
  }

  /**
   * @function removeRule
   * @description Removes a rule at a specific index for the given type.
   * @param {string} ruleType - 'operator' or 'incident'.
   * @param {number} ruleIndex - The index of the rule to remove.
   */
  removeRule (ruleType, ruleIndex) {
    const ruleKey = this.getRulesKey(ruleType);
    this.setState(prevState => {
      const updatedRules = [...this.getRulesForTypeFromSettings(prevState.settings, ruleType, prevState.activeProfileIndexes)];
      updatedRules.splice(ruleIndex, 1);
      return {
        settings: this.updateRulesForType(prevState.settings, ruleType, updatedRules),
        validationErrors: _.omit(prevState.validationErrors, ruleKey), // Clear errors for this rule set
      };
    });
  }

  /**
   * @function addCondition
   * @description Adds a new default condition to a specific rule.
   * @param {string} ruleType - 'operator' or 'incident'.
   * @param {number} ruleIndex - The index of the rule.
   */
  addCondition (ruleType, ruleIndex) {
    const newCondition = this.getDefaultCondition(ruleType);
    const ruleKey = this.getRulesKey(ruleType);

    this.setState(prevState => {
      const updatedRules = _.cloneDeep(this.getRulesForTypeFromSettings(prevState.settings, ruleType, prevState.activeProfileIndexes));
      if (updatedRules[ruleIndex]) {
        if (!updatedRules[ruleIndex].conditions) {
          updatedRules[ruleIndex].conditions = []; // Initialize if missing
        }
        updatedRules[ruleIndex].conditions.push(newCondition);
      }
      return {
        settings: this.updateRulesForType(prevState.settings, ruleType, updatedRules),
        validationErrors: _.omit(prevState.validationErrors, `${ruleKey}.${ruleIndex}.conditions`), // Clear errors for this rule's conditions
      };
    });
  }


  /**
   * @function removeCondition
   * @description Removes a condition from a specific rule.
   * @param {string} ruleType - 'operator' or 'incident'.
   * @param {number} ruleIndex - The index of the rule.
   * @param {number} conditionIndex - The index of the condition to remove.
   */
  removeCondition (ruleType, ruleIndex, conditionIndex) {
    const ruleKey = this.getRulesKey(ruleType);
    this.setState(prevState => {
      const updatedRules = _.cloneDeep(this.getRulesForTypeFromSettings(prevState.settings, ruleType, prevState.activeProfileIndexes));
      if (updatedRules[ruleIndex] && updatedRules[ruleIndex].conditions) {
        updatedRules[ruleIndex].conditions.splice(conditionIndex, 1);
      }
      return {
        settings: this.updateRulesForType(prevState.settings, ruleType, updatedRules),
        validationErrors: _.omit(prevState.validationErrors, `${ruleKey}.${ruleIndex}.conditions`),
      };
    });
  }

  /**
   * @function addChange
   * @description Adds a new default change to a specific rule.
   * @param {string} ruleType - 'operator' or 'incident'.
   * @param {number} ruleIndex - The index of the rule.
   */
  addChange (ruleType, ruleIndex) {
    const newChange = this.getDefaultChange(ruleType);
    const ruleKey = this.getRulesKey(ruleType);
    this.setState(prevState => {
      const updatedRules = _.cloneDeep(this.getRulesForTypeFromSettings(prevState.settings, ruleType, prevState.activeProfileIndexes));
      if (updatedRules[ruleIndex]) {
        if (!updatedRules[ruleIndex].changes) {
          updatedRules[ruleIndex].changes = []; // Initialize if missing
        }
        updatedRules[ruleIndex].changes.push(newChange);
      }
      return {
        settings: this.updateRulesForType(prevState.settings, ruleType, updatedRules),
        validationErrors: _.omit(prevState.validationErrors, `${ruleKey}.${ruleIndex}.changes`), // Clear errors for this rule's changes
      };
    });
  }

  /**
   * @function removeChange
   * @description Removes a change from a specific rule.
   * @param {string} ruleType - 'operator' or 'incident'.
   * @param {number} ruleIndex - The index of the rule.
   * @param {number} changeIndex - The index of the change to remove.
   */
  removeChange (ruleType, ruleIndex, changeIndex) {
    const ruleKey = this.getRulesKey(ruleType);
    this.setState(prevState => {
      const updatedRules = _.cloneDeep(this.getRulesForTypeFromSettings(prevState.settings, ruleType, prevState.activeProfileIndexes));
      if (updatedRules[ruleIndex] && updatedRules[ruleIndex].changes) {
        updatedRules[ruleIndex].changes.splice(changeIndex, 1);
      }
      return {
        settings: this.updateRulesForType(prevState.settings, ruleType, updatedRules),
        validationErrors: _.omit(prevState.validationErrors, `${ruleKey}.${ruleIndex}.changes`),
      };
    });
  }


  /**
   * @function handleRuleUpdate
   * @description Updates a specific property of a condition or change within a rule.
   * @param {string} ruleType - 'operator' or 'incident'.
   * @param {number} ruleIndex - Index of the rule.
   * @param {'conditions' | 'changes'} section - 'conditions' or 'changes'.
   * @param {number} itemIndex - Index of the condition/change within the section.
   * @param {string} property - The property name ('field', 'operator', 'value').
   * @param {Event | string} eventOrValue - The change event or the direct value (for selects).
   */
  handleRuleUpdate (ruleType, ruleIndex, section, itemIndex, property, eventOrValue) {
    const value = eventOrValue && eventOrValue.target !== undefined
      ? eventOrValue.target.value
      : eventOrValue;
    const validationBaseKey = `${ruleType}Rules`; // Need this for error path

    this.setState(prevState => {
      const updatedRules = _.cloneDeep(this.getRulesForTypeFromSettings(prevState.settings, ruleType, prevState.activeProfileIndexes));
      if (updatedRules[ruleIndex] && updatedRules[ruleIndex][section] && updatedRules[ruleIndex][section][itemIndex]) {
        updatedRules[ruleIndex][section][itemIndex][property] = value;

        if (section === 'conditions' && property === 'field') {
          const fieldDefinition = this.getConditionFieldDefinition(ruleType, value);
          const allowedOperators = getOperatorsForFieldDefinition(fieldDefinition);
          updatedRules[ruleIndex][section][itemIndex].operator = allowedOperators[0] || '==';
          updatedRules[ruleIndex][section][itemIndex].value = '';
        }
      }
      return {
        settings: this.updateRulesForType(prevState.settings, ruleType, updatedRules),
      };
    }, () => {
      // Callback after state update: Clear validation errors for the updated item
      this.setState(prevState => {
        const errorPath = `${validationBaseKey}.${ruleIndex}.${section}.${itemIndex}.${property}`;
        let newValidationErrors = _.omit(prevState.validationErrors, errorPath);

        // Also attempt to clear parent item error if all its child errors are gone
        const parentItemPath = `${validationBaseKey}.${ruleIndex}.${section}.${itemIndex}`;
        if (_.isEmpty(_.get(newValidationErrors, parentItemPath))) {
          const clonedErrors = _.cloneDeep(newValidationErrors);
          _.unset(clonedErrors, parentItemPath);
          newValidationErrors = clonedErrors;
        }
        // Clear rule-level error if section is now valid
        const parentRulePath = `${validationBaseKey}.${ruleIndex}.${section}`;
        if (_.isEmpty(_.get(newValidationErrors, parentRulePath))) {
          const clonedErrors = _.cloneDeep(newValidationErrors);
          _.unset(clonedErrors, parentRulePath);
          newValidationErrors = clonedErrors;
        }
        // Clear top-level rule error if rule is now valid
        const topRulePath = `${validationBaseKey}.${ruleIndex}`;
        if (_.isEmpty(_.get(newValidationErrors, topRulePath))) {
          const clonedErrors = _.cloneDeep(newValidationErrors);
          _.unset(clonedErrors, topRulePath);
          newValidationErrors = clonedErrors;
        }

        // Further clear the top-level key if the whole ruleset is now valid
        if (_.isEmpty(_.get(newValidationErrors, validationBaseKey))) {
          const clonedErrors = _.cloneDeep(newValidationErrors);
          _.unset(clonedErrors, validationBaseKey);
          newValidationErrors = clonedErrors;
        }

        // Only update validationErrors state if it actually changed
        if (!_.isEqual(newValidationErrors, prevState.validationErrors)) {
          return { validationErrors: newValidationErrors };
        }
        return null; // No state update needed if errors didn't change
      });
    });
  }


  // --- BEGIN: Added Default Change Handlers ---
  /**
   * @function addDefaultChange
   * @description Adds a new default change item.
   * @param {string} changeType - 'operator' or 'incident'.
   */
  addDefaultChange (changeType) {
    const newChange = changeType === 'operator' ? _.cloneDeep(DEFAULT_OPERATOR_CHANGE) :
      changeType === 'incident' ? _.cloneDeep(DEFAULT_INCIDENT_CHANGE) :
        changeType === 'person' ? _.cloneDeep(DEFAULT_PERSON_CHANGE) :
          changeType === 'change' ? _.cloneDeep(DEFAULT_CHANGE_CHANGE) :
            {}; // Fallback
    const changeKey = `${changeType}DefaultChanges`;

    this.setState(prevState => ({
      settings: {
        ...prevState.settings,
        [changeKey]: [...(prevState.settings[changeKey] || []), newChange],
      },
      validationErrors: _.omit(prevState.validationErrors, changeKey),
    }));
  }

  /**
   * @function removeDefaultChange
   * @description Removes a default change item at a specific index.
   * @param {string} changeType - 'operator' or 'incident'.
   * @param {number} index - The index of the default change to remove.
   */
  removeDefaultChange (changeType, index) {
    const changeKey = `${changeType}DefaultChanges`;
    this.setState(prevState => {
      const updatedChanges = [...(prevState.settings[changeKey] || [])];
      updatedChanges.splice(index, 1);
      return {
        settings: {
          ...prevState.settings,
          [changeKey]: updatedChanges,
        },
        validationErrors: _.omit(prevState.validationErrors, changeKey),
      };
    });
  }

  /**
   * @function handleDefaultChangeUpdate
   * @description Updates a specific property of a default change item.
   * @param {string} changeType - 'operator' or 'incident'.
   * @param {number} index - Index of the default change item.
   * @param {string} property - The property name ('field', 'value').
   * @param {Event | string} eventOrValue - The change event or the direct value.
   */
  handleDefaultChangeUpdate (changeType, index, property, eventOrValue) {
    const changeKey = `${changeType}DefaultChanges`;
    const value = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue.target.value;
    const validationBaseKey = `${changeType}DefaultChanges`; // Need this for error path

    this.setState(prevState => {
      const updatedChanges = _.cloneDeep(prevState.settings[changeKey] || []);
      if (updatedChanges[index]) {
        updatedChanges[index][property] = value;
      }
      return {
        settings: {
          ...prevState.settings,
          [changeKey]: updatedChanges,
        },
      };
    }, () => {
      // Callback after state update: Clear validation errors for the updated item
      this.setState(prevState => {
        const errorPath = `${validationBaseKey}.${index}.${property}`;
        let newValidationErrors = _.omit(prevState.validationErrors, errorPath);

        // Also attempt to clear parent item error if all its child errors are gone
        const parentItemPath = `${validationBaseKey}.${index}`;
        if (_.isEmpty(_.get(newValidationErrors, parentItemPath))) {
          const clonedErrors = _.cloneDeep(newValidationErrors);
          _.unset(clonedErrors, parentItemPath);
          newValidationErrors = clonedErrors;
        }
        // Clear rule-level error if section is now valid
        const parentRulePath = `${validationBaseKey}.${index}`;
        if (_.isEmpty(_.get(newValidationErrors, parentRulePath))) {
          const clonedErrors = _.cloneDeep(newValidationErrors);
          _.unset(clonedErrors, parentRulePath);
          newValidationErrors = clonedErrors;
        }
        // Clear top-level rule error if rule is now valid
        const topRulePath = `${validationBaseKey}.${index}`;
        if (_.isEmpty(_.get(newValidationErrors, topRulePath))) {
          const clonedErrors = _.cloneDeep(newValidationErrors);
          _.unset(clonedErrors, topRulePath);
          newValidationErrors = clonedErrors;
        }

        // Further clear the top-level key if the whole ruleset is now valid
        if (_.isEmpty(_.get(newValidationErrors, validationBaseKey))) {
          const clonedErrors = _.cloneDeep(newValidationErrors);
          _.unset(clonedErrors, validationBaseKey);
          newValidationErrors = clonedErrors;
        }

        // Only update validationErrors state if it actually changed
        if (!_.isEqual(newValidationErrors, prevState.validationErrors)) {
          return { validationErrors: newValidationErrors };
        }
        return null; // No state update needed if errors didn't change
      });
    });
  }
  // --- END: Added Default Change Handlers ---


  /**
   * @function cleanEmpty
   * @description Recursively removes null or empty string values from an object, with exceptions.
   *              Also removes empty plain objects, but preserves empty arrays. Used before saving.
   *              Explicitly preserves critical fields like useReplacementCaller.
   * @param {object|array} obj - The object or array to clean.
   * @returns {object|array} The cleaned object or array.
   */
  cleanEmpty = (obj) => {
    // Define fields that should ALWAYS be preserved, even if they're falsey
    const PRESERVED_FIELDS = [
      'useReplacementCaller', 
      'replacementCallerId', 
      'operatorAnonymizationEnabled',
      'incidentProcessingEnabled', 
      'personProcessingEnabled',
      'changeProcessingEnabled',
      'toDeleteAttachment',
    ];

    // For arrays, clean each item recursively but keep the array structure
    if (_.isArray(obj)) {
      return obj.map(item => this.cleanEmpty(item)).filter(item => 
        item !== null && 
        item !== undefined && 
        item !== '' && 
        // Keep non-empty objects in arrays
        !(_.isObject(item) && !_.isArray(item) && !React.isValidElement(item) && _.isEmpty(item))
      );
    } else if (_.isObject(obj) && !_.isArray(obj) && !React.isValidElement(obj)) {
      // For objects (but not arrays or React elements), recursively clean each property
      // First, recursively clean all values
      const cleanedObj = {};
      
      // Process each key in the object
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        
        // Check if this is a preserved field
        const isPreservedField = PRESERVED_FIELDS.includes(key);
        
        // For preserved fields, always keep them regardless of value
        if (isPreservedField) {
          cleanedObj[key] = value; // Keep as is, don't clean
        } else {
          // For all other fields, recursively clean values
          const cleanedValue = this.cleanEmpty(value);
          
          // Include the cleaned value only if it's not empty (unless it's a preserved field)
          if (cleanedValue !== null && 
              cleanedValue !== undefined && 
              cleanedValue !== '' && 
              !(_.isObject(cleanedValue) && !_.isArray(cleanedValue) && _.isEmpty(cleanedValue))) {
            cleanedObj[key] = cleanedValue;
          }
        }
      });
      
      return cleanedObj;
    }
    
    // Return primitive values unchanged
    return obj;
  };


  /**
   * @function renderRuleEditor
   * @description Renders the UI for editing rules (conditions and changes).
   * @param {string} ruleType - 'operator' or 'incident'.
   * @returns {React.ReactNode} JSX for the rule editor section.
   */
  renderRuleEditor (ruleType) {
    const rules = this.getRulesForType(ruleType);
    const conditionFieldOptions = this.getConditionFieldOptions(ruleType);
    const changeFieldOptions = this.getChangeFieldOptions(ruleType);
    const validationBaseKey = `${ruleType}Rules`; // Base key for validation errors
      
    // Get the TOPdesk endpoint ID for person selectors
    const topdeskEndpoint = Array.isArray(this.props.endpointsByType?.topdesk) 
      ? this.props.endpointsByType.topdesk[0] 
      : this.props.endpointsByType?.topdesk;
    const topdeskEndpointId = topdeskEndpoint?.id;

    /**
       * @function getError
       * @description Helper to get validation error message for a specific field.
       * @param {number} ruleIndex - Index of the rule.
       * @param {'conditions' | 'changes'} section - Section type.
       * @param {number} itemIndex - Index within the section.
       * @param {string} property - Property name ('field', 'operator', 'value').
       * @returns {string|null} Error message or null.
       */
    const getError = (ruleIndex, section, itemIndex, property) => {
      return _.get(this.state.validationErrors, `${validationBaseKey}.${ruleIndex}.${section}.${itemIndex}.${property}`, null);
    };

    return (
      <div className="eatd-section-card eatd-section-card--soft">
        <div className="eatd-section-header">
          <div>
            <h5 className="eatd-section-title">Scenarios</h5>
            <p className="eatd-section-description">
              Match conditions and apply changes.
            </p>
          </div>
        </div>
        {rules.map((rule, ruleIndex) => (
          <div key={`rule-${ruleType}-${ruleIndex}`} className="card mb-3 eatd-rule-card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span>Scenario #{ruleIndex + 1}</span>
              <button
                type="button"
                className="btn btn-danger btn-sm eatd-action-button"
                onClick={() => this.removeRule(ruleType, ruleIndex)}
                title="Remove Scenario"
              >
                <span className="mdi mdi-delete"></span>
              </button>
            </div>
            <div className="card-body">
              {/* --- Conditions --- */}
              <h6>When</h6>
              {(rule.conditions || []).length === 0 && <p className="text-muted small eatd-compact-note">No conditions. This scenario always applies.</p>}
              {(rule.conditions || []).map((condition, conditionIndex) => {
                const conditionFieldDefinition = this.getConditionFieldDefinition(ruleType, condition.field);
                const conditionInputType = conditionFieldDefinition?.inputType || 'text';
                const conditionValueOptions = this.getConditionValueOptions(conditionFieldDefinition);
                const conditionOperators = this.getConditionOperators(ruleType, condition.field);
                const usesOptionDropdown = ['enum', 'topdeskSelect'].includes(conditionInputType);
                const conditionValueOptionSignature = buildOptionSignature(conditionValueOptions);

                return (
                  <div key={`condition-${ruleType}-${ruleIndex}-${conditionIndex}`} className="eatd-rule-item mb-2">
                    <div className="row align-items-center">
                      <div className="col-md-4 mb-2 mb-md-0">
                        <Select2Wrap
                          key={`s2-cond-field-${ruleType}-${ruleIndex}-${conditionIndex}-${condition.field || 'empty'}`}
                          name={`condition-field-${ruleIndex}-${conditionIndex}`}
                          defaultValue={condition.field || ''}
                          data={conditionFieldOptions}
                          onChange={selectedValue => this.handleRuleUpdate(ruleType, ruleIndex, 'conditions', conditionIndex, 'field', selectedValue)}
                          placeholder="Select Field"
                          className={getError(ruleIndex, 'conditions', conditionIndex, 'field') ? 'is-invalid' : ''}
                        />
                        {getError(ruleIndex, 'conditions', conditionIndex, 'field') && <div className="invalid-feedback">{getError(ruleIndex, 'conditions', conditionIndex, 'field')}</div>}
                      </div>
                      <div className="col-md-2 mb-2 mb-md-0">
                        <select
                          className={`form-control ${getError(ruleIndex, 'conditions', conditionIndex, 'operator') ? 'is-invalid' : ''}`}
                          value={condition.operator}
                          onChange={e => this.handleRuleUpdate(ruleType, ruleIndex, 'conditions', conditionIndex, 'operator', e)}
                        >
                          {conditionOperators.map(op => <option key={op} value={op}>{op}</option>)}
                        </select>
                        {getError(ruleIndex, 'conditions', conditionIndex, 'operator') && <div className="invalid-feedback">{getError(ruleIndex, 'conditions', conditionIndex, 'operator')}</div>}
                      </div>
                      <div className="col-md-4 mb-2 mb-md-0">
                        {usesOptionDropdown ? (
                          <>
                            <Select2Wrap
                              key={`s2-cond-value-${ruleType}-${ruleIndex}-${conditionIndex}-${condition.field || 'empty'}-${condition.value ?? 'empty'}-${conditionValueOptionSignature}-${this.state.topdeskConditionOptionsLoading ? 'loading' : 'ready'}`}
                              name={`condition-value-${ruleIndex}-${conditionIndex}`}
                              defaultValue={condition.value ?? ''}
                              data={[{ id: '', text: 'Select Value' }, ...conditionValueOptions]}
                              options={{ minimumResultsForSearch: 0 }}
                              onChange={selectedValue => this.handleRuleUpdate(ruleType, ruleIndex, 'conditions', conditionIndex, 'value', selectedValue)}
                              placeholder={this.state.topdeskConditionOptionsLoading && conditionInputType === 'topdeskSelect' ? 'Loading TOPdesk values...' : 'Select Value'}
                              className={getError(ruleIndex, 'conditions', conditionIndex, 'value') ? 'is-invalid' : ''}
                            />
                            {!this.getTopdeskEndpointId() && conditionInputType === 'topdeskSelect' && (
                              <div className="text-warning small mt-1">
                                TOPdesk endpoint not found. Please configure TOPdesk in the Credentials tab.
                              </div>
                            )}
                          </>
                        ) : (
                          <input
                            type={conditionInputType === 'number' ? 'number' : 'text'}
                            className={`form-control ${getError(ruleIndex, 'conditions', conditionIndex, 'value') ? 'is-invalid' : ''}`}
                            placeholder={conditionInputType === 'reference' ? 'TOPdesk value' : 'Value'}
                            value={condition.value ?? ''}
                            onChange={e => this.handleRuleUpdate(
                              ruleType,
                              ruleIndex,
                              'conditions',
                              conditionIndex,
                              'value',
                              conditionInputType === 'number'
                                ? (e.target.value === '' ? '' : Number(e.target.value))
                                : e,
                            )}
                          />
                        )}
                        {getError(ruleIndex, 'conditions', conditionIndex, 'value') && <div className="invalid-feedback">{getError(ruleIndex, 'conditions', conditionIndex, 'value')}</div>}
                      </div>
                      <div className="col-md-2 text-right">
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm eatd-action-button"
                          onClick={() => this.removeCondition(ruleType, ruleIndex, conditionIndex)}
                          title="Remove When Condition"
                        >
                          <span className="mdi mdi-minus-circle-outline"></span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm eatd-action-button eatd-inline-action eatd-inline-action--spaced"
                onClick={() => this.addCondition(ruleType, ruleIndex)}
              >
                <span className="mdi mdi-plus-circle-outline mr-1"></span> Add When
              </button>

              {/* --- Changes --- */}
              <h6 className="mt-3">Then</h6>
              {(rule.changes || []).length === 0 && <p className="text-muted small eatd-compact-note">No actions yet.</p>}
              {(rule.changes || []).map((change, changeIndex) => (
                <div key={`change-${ruleType}-${ruleIndex}-${changeIndex}`} className="eatd-rule-item mb-2">
                  <div className="row align-items-center">
                    <div className="col-md-5 mb-2 mb-md-0">
                      <Select2Wrap
                        key={`s2-change-field-${ruleType}-${ruleIndex}-${changeIndex}-${change.field || 'empty'}`}
                        name={`change-field-${ruleIndex}-${changeIndex}`}
                        defaultValue={change.field || ''}
                        data={changeFieldOptions}
                        onChange={selectedValue => this.handleRuleUpdate(ruleType, ruleIndex, 'changes', changeIndex, 'field', selectedValue)}
                        placeholder="Select Field to Change"
                        className={getError(ruleIndex, 'changes', changeIndex, 'field') ? 'is-invalid' : ''}
                      />
                      {getError(ruleIndex, 'changes', changeIndex, 'field') && <div className="invalid-feedback">{getError(ruleIndex, 'changes', changeIndex, 'field')}</div>}
                                          
                      {/* Special warning for conflicting caller fields */}
                      {change.field === 'caller' && this.hasConflictingCallerFields(ruleType, ruleIndex, changeIndex) && (
                        <div className="text-warning small mt-1">
                          <strong>Warning:</strong> This scenario also has other caller-related fields that will be overridden by this complete caller replacement.
                        </div>
                      )}
                                          
                      {/* Also warn for caller.* fields if 'caller' field exists */}
                      {change.field && change.field.startsWith('caller.') && 
                                           this.hasConflictingCallerFields(ruleType, ruleIndex, changeIndex) && (
                        <div className="text-warning small mt-1">
                          <strong>Warning:</strong> This scenario also has a 'caller' field that will override this change.
                        </div>
                      )}
                    </div>
                                      
                    {/* Special handling for 'caller' field - show person selector instead of text input */}
                    {ruleType === 'incident' && change.field === 'caller' ? (
                      <div className="col-md-5 mb-2 mb-md-0">
                        {topdeskEndpointId ? (
                          (() => { // IIFE for logging
                            const originalCallerValue = change.value;
                            const parsedCallerValue = this.parseCallerValue(originalCallerValue);
                            const keyForSearcher = `caller-selector-${ruleType}-${ruleIndex}-${changeIndex}-${parsedCallerValue || 'empty'}`;
                                             

                            return (
                              <div>
                                <EndpointTopdeskRemoteSearcher
                                  key={keyForSearcher}
                                  id={`caller-selector-${ruleType}-${ruleIndex}-${changeIndex}`}
                                  name={`caller-value-${ruleIndex}-${changeIndex}`}
                                  endpointId={topdeskEndpointId}
                                  forWhat="person"
                                  onChange={(value) => this.handleCallerPersonSelected(ruleType, ruleIndex, changeIndex, value)}
                                  options={{
                                    allowClear: true,
                                    placeholder: 'Search for a TOPdesk person...',
                                    minimumInputLength: 1,
                                  }}
                                  value={parsedCallerValue} // Use parsed value
                                  defaultValue={parsedCallerValue} // Pass parsed value to defaultValue for initial load
                                />
                                <div className="text-info small mt-1">
                                  <strong>Special field:</strong> This will completely replace the original caller with the selected person.
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <div className="alert alert-warning py-1 px-2">
                                              TOPdesk endpoint not found. Please configure TOPdesk in the Credentials tab.
                          </div>
                        )}
                      </div>
                    ) : (ruleType === 'change' && ['requester', 'operator', 'operatorGroup'].includes(change.field)) ? (
                      <div className="col-md-5 mb-2 mb-md-0">
                        {topdeskEndpointId ? (
                          <EndpointTopdeskRemoteSearcher
                            key={`change-id-selector-${ruleType}-${ruleIndex}-${changeIndex}-${change.field}-${change.value || 'empty'}`}
                            id={`change-id-selector-${ruleType}-${ruleIndex}-${changeIndex}`}
                            name={`change-id-value-${ruleIndex}-${changeIndex}`}
                            endpointId={topdeskEndpointId}
                            forWhat={change.field === 'operator' ? 'operator' : (change.field === 'operatorGroup' ? 'operatorGroups' : 'person')}
                            onChange={(value) => this.handleChangeIdSelected(ruleType, ruleIndex, changeIndex, value)}
                            options={{ allowClear: true, placeholder: `Select ${change.field}...`, minimumInputLength: 1 }}
                            value={this.parseCallerValue(change.value)}
                            defaultValue={this.parseCallerValue(change.value)}
                          />
                        ) : (
                          <div className="alert alert-warning py-1 px-2">TOPdesk endpoint not found. Please configure TOPdesk in the Credentials tab.</div>
                        )}
                      </div>
                    ) : (
                    // Regular text input for other fields
                      <div className="col-md-5 mb-2 mb-md-0">
                        <input
                          type="text"
                          className={`form-control ${getError(ruleIndex, 'changes', changeIndex, 'value') ? 'is-invalid' : ''}`}
                          placeholder="New Value"
                          value={change.value}
                          onChange={e => this.handleRuleUpdate(ruleType, ruleIndex, 'changes', changeIndex, 'value', e)}
                        />
                        {getError(ruleIndex, 'changes', changeIndex, 'value') && <div className="invalid-feedback">{getError(ruleIndex, 'changes', changeIndex, 'value')}</div>}
                      </div>
                    )}
                                      
                    <div className="col-md-2 text-right">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm eatd-action-button"
                        onClick={() => this.removeChange(ruleType, ruleIndex, changeIndex)}
                        title="Remove Then Action"
                      >
                        <span className="mdi mdi-minus-circle-outline"></span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm eatd-action-button eatd-inline-action"
                onClick={() => this.addChange(ruleType, ruleIndex)}
              >
                <span className="mdi mdi-plus-circle-outline mr-1"></span> Add Then
              </button>
            </div> {/* /.card-body */}
          </div> // /.card
        ))}
        <button type="button" className="btn btn-primary eatd-action-button" onClick={() => this.addRule(ruleType)}>
          <span className="mdi mdi-plus mr-1"></span> Add Scenario
        </button>
      </div>
    );
  }

  /**
   * @function formatCallerValue
   * @description Formats a caller ID into the proper format required by the API
   * @param {string} callerId - The ID of the caller
   * @returns {string} Properly formatted caller string
   */
  formatCallerValue = (callerId) => {
    if (!callerId) return '';
    
    // Format using the special syntax: id:{callerId}
    // The proper format is a string: "id:{callerId}" with no quotes around the ID itself
    return `id:{${callerId}}`;
  };

  /**
   * @function handleChangeIdSelected
   * @description Generic handler for change-rule id-object fields (requester/operator/operatorGroup)
   * @param {string} ruleType - Rule type being edited.
   * @param {number} ruleIndex - Index of the rule.
   * @param {number} changeIndex - Index of the change within the rule.
   * @param {string|object|null} selected - Selected TOPdesk object or raw id.
   * @returns {void}
   */
  handleChangeIdSelected = (ruleType, ruleIndex, changeIndex, selected) => {
    const idToFormat = (typeof selected === 'object' && selected !== null && selected.id) ? selected.id : selected;
    const wrapped = idToFormat ? this.formatCallerValue(String(idToFormat)) : '';
    this.setState(prevState => {
      const updatedRules = _.cloneDeep(this.getRulesForTypeFromSettings(prevState.settings, ruleType, prevState.activeProfileIndexes));
      if (updatedRules[ruleIndex] && updatedRules[ruleIndex].changes && updatedRules[ruleIndex].changes[changeIndex]) {
        updatedRules[ruleIndex].changes[changeIndex].value = wrapped;
      }
      return {
        settings: this.updateRulesForType(prevState.settings, ruleType, updatedRules),
      };
    });
  };

  /**
   * @function handleDefaultChangeIdSelected
   * @description Generic handler for default change id-object fields (requester/operator/operatorGroup)
   * @param {string} changeType - Default change collection being edited.
   * @param {number} index - Index of the default change.
   * @param {string|object|null} selected - Selected TOPdesk object or raw id.
   * @returns {void}
   */
  handleDefaultChangeIdSelected = (changeType, index, selected) => {
    const idToFormat = (typeof selected === 'object' && selected !== null && selected.id) ? selected.id : selected;
    const wrapped = idToFormat ? this.formatCallerValue(String(idToFormat)) : '';
    this.setState(prevState => {
      const updatedChanges = _.cloneDeep(prevState.settings[`${changeType}DefaultChanges`] || []);
      if (updatedChanges[index]) {
        updatedChanges[index].value = wrapped;
      }
      return {
        settings: {
          ...prevState.settings,
          [`${changeType}DefaultChanges`]: updatedChanges,
        },
      };
    });
  };

  /**
   * @function parseCallerValue
   * @description Extracts the plain ID from a formatted caller string (e.g., "id:{GUID}" -> "GUID").
   * @param {string} formattedValue - The potentially formatted caller value.
   * @returns {string} The plain ID or the original string if not in the expected format or if not a string.
   */
  parseCallerValue = (formattedValue) => {
    if (typeof formattedValue === 'string') {
      const match = formattedValue.match(/^id:\{(.*)\}$/); // Adjusted regex to match curly braces
      if (match && match[1]) {
        return match[1]; // Return the captured GUID
      }
    }
    return formattedValue; // Return original if not matched or not a string
  };

  /**
   * @function handleCallerPersonSelected
   * @description Handles the selection of a person from the TOPdesk person selector specifically for caller replacement in rules.
   * @param {string} ruleType - 'incident'
   * @param {number} ruleIndex - Index of the rule.
   * @param {number} changeIndex - Index of the change.
   * @param {string|null} personId - Selected person ID.
   */
  handleCallerPersonSelected = (ruleType, ruleIndex, changeIndex, personId) => {
    // console.log("[EATDOptionsForm handleCallerPersonSelected] Args:", {
    //   ruleType,
    //   ruleIndex,
    //   changeIndex,
    //   personId // This is the value from EndpointTopdeskRemoteSearcher's onChange
    // });
    
    // Ensure personId is a string if it's an object with an 'id' property (though typically it should be a string ID already)
    const idToFormat = (typeof personId === 'object' && personId !== null && personId.id) ? personId.id : personId;
    // console.log("[EATDOptionsForm handleCallerPersonSelected] ID to format:", idToFormat);

    // Format caller value - properly formatted as id:{callerId}
    const callerValue = idToFormat ? this.formatCallerValue(String(idToFormat)) : ''; // Ensure it's a string before formatting
    
    // console.log("[EATDOptionsForm handleCallerPersonSelected] Formatted caller value to store in state:", callerValue);
    
    // Update the rule change with the formatted caller value
    this.setState(prevState => {
      const updatedRules = _.cloneDeep(this.getRulesForTypeFromSettings(prevState.settings, ruleType));
      if (updatedRules[ruleIndex] && updatedRules[ruleIndex].changes && updatedRules[ruleIndex].changes[changeIndex]) {
        updatedRules[ruleIndex].changes[changeIndex].value = callerValue;
      }
      return {
        settings: this.updateRulesForType(prevState.settings, ruleType, updatedRules),
      };
    });
  };

  /**
   * @function hasConflictingCallerFields
   * @description Checks if there are other caller.* fields in the same rule changes that would conflict with 'caller'.
   * @param {string} ruleType - 'incident'
   * @param {number} ruleIndex - Index of the rule.
   * @param {number} currentChangeIndex - Index of the current change to exclude from check.
   * @returns {boolean} True if there are conflicting fields.
   */
  hasConflictingCallerFields = (ruleType, ruleIndex, currentChangeIndex) => {
    const rules = this.getRulesForType(ruleType);
    const rule = rules[ruleIndex];
    
    if (!rule || !rule.changes) return false;
    
    return rule.changes.some((change, idx) => {
      // Skip the current change
      if (idx === currentChangeIndex) return false;
      
      // Check if any other change starts with 'caller.' or is 'caller'
      return change.field && (
        change.field === 'caller' || 
        change.field.startsWith('caller.')
      );
    });
  };

  /**
   * @function handleDefaultCallerPersonSelected
   * @description Handles the selection of a person from the TOPdesk person selector for default changes.
   * @param {string} changeType - 'incident'
   * @param {number} index - Index of the default change.
   * @param {string|null} personId - Selected person ID.
   */
  handleDefaultCallerPersonSelected = (changeType, index, personId) => {
    // console.log("[EATDOptionsForm handleDefaultCallerPersonSelected] Args:", {
    //   changeType,
    //   index,
    //   personId // This is the value from EndpointTopdeskRemoteSearcher's onChange
    // });

    // Ensure personId is a string if it's an object with an 'id' property
    const idToFormat = (typeof personId === 'object' && personId !== null && personId.id) ? personId.id : personId;
    // console.log("[EATDOptionsForm handleDefaultCallerPersonSelected] ID to format:", idToFormat);
    
    // Format caller value - properly formatted as id:{callerId}
    const callerValue = idToFormat ? this.formatCallerValue(String(idToFormat)) : ''; // Ensure it's a string
    
    // console.log("[EATDOptionsForm handleDefaultCallerPersonSelected] Formatted default caller value to store in state:", callerValue);
    
    // Update the default change with the formatted caller value
    this.setState(prevState => {
      const updatedChanges = _.cloneDeep(prevState.settings[`${changeType}DefaultChanges`] || []);
      if (updatedChanges[index]) {
        updatedChanges[index].value = callerValue;
      }
      return {
        settings: {
          ...prevState.settings,
          [`${changeType}DefaultChanges`]: updatedChanges,
        },
      };
    });
  };

  /**
   * @function hasConflictingDefaultCallerFields
   * @description Checks if there are other caller.* fields in default changes that would conflict with 'caller'.
   * @param {string} changeType - 'incident'
   * @param {number} currentIndex - Index of the current change to exclude from check.
   * @returns {boolean} True if there are conflicting fields.
   */
  hasConflictingDefaultCallerFields = (changeType, currentIndex) => {
    const changes = this.state.settings[`${changeType}DefaultChanges`] || [];
    
    return changes.some((change, idx) => {
      // Skip the current change
      if (idx === currentIndex) return false;
      
      // Check if any other change starts with 'caller.' or is 'caller'
      return change.field && (
        change.field === 'caller' || 
        change.field.startsWith('caller.')
      );
    });
  };

  /**
   * @function renderDefaultChangeEditor
   * @description Renders the UI for editing default changes.
   * @param {string} changeType - 'operator' or 'incident'.
   * @returns {React.ReactNode} JSX for the default change editor section.
   */
  renderDefaultChangeEditor (changeType) {
    const changes = this.state.settings[`${changeType}DefaultChanges`] || [];
    const changeFieldOptions = this.getChangeFieldOptions(changeType);
    const validationBaseKey = `${changeType}DefaultChanges`;

    // Get the TOPdesk endpoint ID for person selectors
    const topdeskEndpoint = Array.isArray(this.props.endpointsByType?.topdesk) 
      ? this.props.endpointsByType.topdesk[0] 
      : this.props.endpointsByType?.topdesk;
    const topdeskEndpointId = topdeskEndpoint?.id;

    const getError = (index, property) => {
      return _.get(this.state.validationErrors, `${validationBaseKey}.${index}.${property}`, null);
    };

    return (
      <div className="eatd-section-card eatd-section-card--soft">
        <div className="eatd-section-header">
          <div>
            <h5 className="eatd-section-title">Default Changes</h5>
            <p className="eatd-section-description">
              Used when no rule updates a field.
            </p>
          </div>
        </div>
        {changes.length === 0 && <p className="text-muted small">No default changes defined.</p>}
        {changes.map((change, index) => (
          <div key={`default-change-${changeType}-${index}`} className="eatd-rule-item mb-2">
            <div className="row align-items-center">
              <div className="col-md-5 mb-2 mb-md-0">
                <Select2Wrap
                  key={`s2-default-change-field-${changeType}-${index}-${change.field || 'empty'}`}
                  name={`default-change-field-${changeType}-${index}`}
                  defaultValue={change.field || ''}
                  data={changeFieldOptions}
                  onChange={selectedValue => this.handleDefaultChangeUpdate(changeType, index, 'field', selectedValue)}
                  placeholder="Select Field to Change"
                  className={getError(index, 'field') ? 'is-invalid' : ''}
                />
                {getError(index, 'field') && <div className="invalid-feedback">{getError(index, 'field')}</div>}
                
                {/* Special warning for conflicting caller fields */}
                {change.field === 'caller' && this.hasConflictingDefaultCallerFields(changeType, index) && (
                  <div className="text-warning small mt-1">
                    <strong>Warning:</strong> Other caller-related fields will be overridden by this complete caller replacement.
                  </div>
                )}
                
                {/* Also warn for caller.* fields if 'caller' field exists */}
                {change.field && change.field.startsWith('caller.') && 
                  this.hasConflictingDefaultCallerFields(changeType, index) && (
                  <div className="text-warning small mt-1">
                    <strong>Warning:</strong> The 'caller' field will override this change.
                  </div>
                )}
              </div>
              
              {/* Special handling for 'caller' field - show person selector instead of text input */}
              {changeType === 'incident' && change.field === 'caller' ? (
                <div className="col-md-5 mb-2 mb-md-0">
                  {topdeskEndpointId ? (
                    (() => { // IIFE for logging
                      const originalCallerValue = change.value;
                      const parsedCallerValue = this.parseCallerValue(originalCallerValue);
                      const keyForSearcher = `default-caller-selector-${changeType}-${index}-${parsedCallerValue || 'empty'}`;
            
                      return (
                        <div>
                          <EndpointTopdeskRemoteSearcher
                            key={keyForSearcher}
                            id={`default-caller-selector-${changeType}-${index}`}
                            name={`default-caller-value-${index}`}
                            endpointId={topdeskEndpointId}
                            forWhat="person"
                            onChange={(value) => this.handleDefaultCallerPersonSelected(changeType, index, value)}
                            options={{
                              allowClear: true,
                              placeholder: 'Search for a TOPdesk person...',
                              minimumInputLength: 1,
                            }}
                            value={parsedCallerValue} // Use parsed value
                            defaultValue={parsedCallerValue} // Pass parsed value to defaultValue for initial load
                          />
                          <div className="text-info small mt-1">
                            <strong>Special field:</strong> This will completely replace the original caller with the selected person.
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="alert alert-warning py-1 px-2">
                      TOPdesk endpoint not found. Please configure TOPdesk in the Credentials tab.
                    </div>
                  )}
                </div>
              ) : (changeType === 'change' && ['requester', 'operator', 'operatorGroup'].includes(change.field)) ? (
                <div className="col-md-5 mb-2 mb-md-0">
                  {topdeskEndpointId ? (
                    <EndpointTopdeskRemoteSearcher
                      key={`default-change-id-selector-${changeType}-${index}-${change.field}-${change.value || 'empty'}`}
                      id={`default-change-id-selector-${changeType}-${index}`}
                      name={`default-change-id-value-${index}`}
                      endpointId={topdeskEndpointId}
                      forWhat={change.field === 'operator' ? 'operator' : (change.field === 'operatorGroup' ? 'operatorGroups' : 'person')}
                      onChange={(value) => this.handleDefaultChangeIdSelected(changeType, index, value)}
                      options={{ allowClear: true, placeholder: `Select ${change.field}...`, minimumInputLength: 1 }}
                      value={this.parseCallerValue(change.value)}
                      defaultValue={this.parseCallerValue(change.value)}
                    />
                  ) : (
                    <div className="alert alert-warning py-1 px-2">TOPdesk endpoint not found. Please configure TOPdesk in the Credentials tab.</div>
                  )}
                </div>
              ) : (
                // Regular text input for other fields
                <div className="col-md-5 mb-2 mb-md-0">
                  <input
                    type="text"
                    className={`form-control ${getError(index, 'value') ? 'is-invalid' : ''}`}
                    placeholder="Default Value"
                    value={change.value}
                    onChange={e => this.handleDefaultChangeUpdate(changeType, index, 'value', e)}
                  />
                  {getError(index, 'value') && <div className="invalid-feedback">{getError(index, 'value')}</div>}
                </div>
              )}
              
              <div className="col-md-2 text-right">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm eatd-action-button"
                  onClick={() => this.removeDefaultChange(changeType, index)}
                  title="Remove Default Change"
                >
                  <span className="mdi mdi-minus-circle-outline"></span>
                </button>
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-secondary eatd-action-button"
          onClick={() => this.addDefaultChange(changeType)}
        >
          <span className="mdi mdi-plus mr-1"></span> Add Default
        </button>
      </div>
    );
  }

  /**
   * @function handleToggleReplacementCaller
   * @description Toggles the replacement caller feature and clears the ID if disabling.
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @deprecated Use rule-based or default change caller replacement instead
   */
  handleToggleReplacementCaller = (event) => {
    const checked = event.target.checked;
    this.setState((prevState) => ({
      settings: {
        ...prevState.settings,
        useReplacementCaller: checked,
        // Only clear replacementCallerId when disabling
        replacementCallerId: checked ? prevState.settings.replacementCallerId : '',
      },
    }));
  };

  /**
   * @function handleReplacementCallerChange
   * @description Sets the replacementCallerId in settings.
   * @param {string|null} value
   * @deprecated Use rule-based or default change caller replacement instead
   */
  handleReplacementCallerChange = (value) => {
    // console.log("[EATDOptionsForm] Replacement caller changed:", { oldValue: this.state.settings.replacementCallerId, newValue: value });
    
    this.setState((prevState) => ({
      settings: {
        ...prevState.settings,
        replacementCallerId: value || '',
      },
    }));
  };

  renderProcessingProfiles = (ruleType, topdeskEndpointId, statusOptions = [], config = {}) => {
    const {
      showDateField = ['incident', 'change'].includes(ruleType),
      showCleanup = ['incident', 'change'].includes(ruleType),
      showStatuses = ruleType === 'incident',
      showArchivedSettings = ruleType === 'incident',
      ageLabel = 'Minimum Age In Days',
      profileDescription = '',
    } = config;
    const profiles = this.state.settings[this.getProfilesKey(ruleType)] || [];
    const activeIndex = this.state.activeProfileIndexes[ruleType] || 0;
    const activeProfile = profiles[activeIndex];

    return (
      <div className="eatd-section-card mt-4">
        <div className="eatd-section-header">
          <div>
            <h5 className="eatd-section-title">{_.capitalize(ruleType)} Profiles</h5>
            <p className="eatd-section-description">Set filters, cleanup, and rules per profile.</p>
          </div>
          <button type="button" className="btn btn-primary btn-sm eatd-action-button" onClick={() => this.addProfile(ruleType)}>
            <span className="mdi mdi-plus mr-1"></span>
            Add Profile
          </button>
        </div>

        {profiles.length > 0 && (
          <div className="eatd-profile-tabs">
            {profiles.map((profile, index) => (
              <button
                key={profile.id || `${ruleType}-${index}`}
                type="button"
                className={`btn btn-sm eatd-profile-tab ${activeIndex === index ? 'is-active' : ''}`}
                onClick={() => this.selectProfile(ruleType, index)}
              >
                {profile.name || `${_.capitalize(ruleType)} Profile ${index + 1}`}
              </button>
            ))}
          </div>
        )}

        {!activeProfile && (
          <div className="alert alert-info">
            No {ruleType} profiles yet.
          </div>
        )}

        {activeProfile && (
          <div className="eatd-profile-shell">
            <div className="eatd-profile-heading">
              <h6 className="eatd-profile-title">
                {activeProfile.name || `${_.capitalize(ruleType)} Profile ${activeIndex + 1}`}
              </h6>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm eatd-action-button"
                disabled={profiles.length <= 1}
                onClick={() => this.removeProfile(ruleType, activeIndex)}
              >
                Remove
              </button>
            </div>

            <div className="eatd-subsection">
              <h6 className="eatd-subsection-title">Profile Configuration</h6>
              {profileDescription ? (
                <p className="eatd-subsection-description">
                  {profileDescription}
                </p>
              ) : null}
              <div className="row">
                <div className="col-md-4">
                  <label>Profile Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={activeProfile.name || ''}
                    onChange={event => this.updateProfileField(ruleType, 'name', event.target.value)}
                  />
                </div>
                {showDateField && (
                  <div className="col-md-3">
                    <label>Date Field</label>
                    <select
                      className="form-control"
                      value={activeProfile.dateField || 'modificationDate'}
                      onChange={event => this.updateProfileField(ruleType, 'dateField', event.target.value)}
                    >
                      <option value="modificationDate">Modification Date</option>
                      <option value="closedDate">Closed Date</option>
                    </select>
                  </div>
                )}
                <div className="col-md-3">
                  <label>{ageLabel}</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    step="1"
                    value={activeProfile.minAgeDays ?? 0}
                    onChange={event => this.updateProfileField(ruleType, 'minAgeDays', _.parseInt(event.target.value) || 0)}
                  />
                </div>
              </div>

              {showStatuses && (
                <div className="row eatd-profile-detail-row">
                  <div className="col-md-12">
                    <label>Allowed Statuses</label>
                    <select
                      key={`profile-status-${ruleType}-${activeProfile.id || activeIndex}-${(activeProfile.updateStatuses || []).join('|')}`}
                      className="form-control"
                      value={(activeProfile.updateStatuses || [])[0] || ''}
                      onChange={event => this.updateProfileField(
                        ruleType,
                        'updateStatuses',
                        event.target.value ? [event.target.value] : [],
                      )}
                    >
                      <option value="">Select a status...</option>
                      {statusOptions.map(option => {
                        const optionValue = option.value ?? option.id;
                        return (
                          <option key={option.id} value={optionValue}>
                            {option.text}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              )}

              {showArchivedSettings && (
                <div className="row eatd-profile-detail-row">
                  {['incident', 'change'].includes(ruleType) && (
                    <div className="col-md-4">
                      <div className="checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={!!activeProfile.includeArchived}
                            onChange={event => this.updateProfileField(ruleType, 'includeArchived', event.target.checked)}
                          /> {ruleType === 'incident' ? 'Include Archived Incidents' : 'Include Archived Changes'}
                        </label>
                      </div>
                    </div>
                  )}
                  <div className={['incident', 'change'].includes(ruleType) ? 'col-md-8' : 'col-md-12'}>
                    <label>Default Archiving Reason</label>
                    {topdeskEndpointId ? (
                      <EndpointTopdeskRemoteSearcher
                        key={`archiving-reason-selector-${activeProfile.defaultArchivingReason || 'empty'}`}
                        id={`defaultArchivingReason-${activeIndex}`}
                        name={`defaultArchivingReason-${activeIndex}`}
                        endpointId={topdeskEndpointId}
                        forWhat="archivingReason"
                        onChange={value => this.updateProfileField(ruleType, 'defaultArchivingReason', value || '')}
                        options={{
                          allowClear: true,
                          placeholder: 'Select an archiving reason...',
                          minimumInputLength: 0,
                        }}
                        value={activeProfile.defaultArchivingReason || ''}
                        defaultValue={activeProfile.defaultArchivingReason || ''}
                      />
                    ) : (
                      <div className="alert alert-warning py-1 px-2">TOPdesk endpoint not found. Please configure TOPdesk in the Credentials tab.</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {showCleanup && (
              <div className="eatd-subsection">
                <h6 className="eatd-subsection-title">Cleanup</h6>
                <p className="eatd-subsection-description">
                  Choose what to remove after processing.
                </p>
                <div className="checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!activeProfile.cleanup?.deleteAttachments}
                      onChange={event => this.updateProfileField(ruleType, 'cleanup.deleteAttachments', event.target.checked)}
                    /> Delete attachments
                  </label>
                </div>
                <div className="checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!activeProfile.cleanup?.deleteActions}
                      onChange={event => this.updateProfileField(ruleType, 'cleanup.deleteActions', event.target.checked)}
                    /> Delete actions
                  </label>
                </div>
                <div className="checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!activeProfile.cleanup?.deleteRequests}
                      onChange={event => this.updateProfileField(ruleType, 'cleanup.deleteRequests', event.target.checked)}
                    /> Delete requests
                  </label>
                </div>
              </div>
            )}

            <div className="mt-4">
              {this.renderRuleEditor(ruleType)}
            </div>
          </div>
        )}
      </div>
    );
  };

  render () {
    const { isSaving, saveError, endpointsByType } = this.props;
    const { settings, activeTab } = this.state;

    // Destructure only needed settings for easier access in form fields
    const {
      operatorAnonymizationEnabled,
      incidentProcessingEnabled,
      personProcessingEnabled,
      changeProcessingEnabled,
    } = settings;

    // Prepare options for incidentUpdateStatuses select
    // TODO: Fetch actual statuses from somewhere (e.g., Topdesk endpoint or constants)
    const placeholderStatuses = ['Registered', 'InProgress', 'WaitingForUser', 'Resolved', 'Closed'];
    const statusOptions = placeholderStatuses.map(status => ({ id: status, text: status, value: status }));

    // Get the TOPdesk endpoint ID using the same pattern as the working tester component
    const topdeskEndpoint = Array.isArray(endpointsByType?.topdesk) 
      ? endpointsByType.topdesk[0] 
      : endpointsByType?.topdesk;
    const topdeskEndpointId = topdeskEndpoint?.id;

    // console.log("[ConnectedEATDOptionsForm] endpointsByType:", endpointsByType, "Derived ID:", topdeskEndpointId);

    return (
      <form onSubmit={this.handleSave} className="eatd-options-form">
        <style>{EATD_OPTIONS_FORM_STYLES}</style>
        <BeRemoteErrorAlert error={saveError} />

        {/* DEBUGGING BUTTON */}
        <div className="mb-2" style={{ display: 'none' }}>
          <button 
            type="button" 
            className="btn btn-sm btn-info"
            onClick={() => {
              // console.log("CURRENT STATE:", this.state);
              // console.log("CURRENT PROPS:", this.props);
              // console.log("SETTINGS USERC:", settings.useReplacementCaller);
              // console.log("SETTINGS RCI:", settings.replacementCallerId);
              alert('Check console for debug info');
            }}
          >
            Debug Info
          </button>
        </div>

        {/* --- Tab Navigation (using buttons) --- */}
        <div className="eatd-tab-list">
          <button
            type="button"
            className={`eatd-tab-button ${activeTab === 'operator' ? 'is-active' : ''}`}
            onClick={() => {
              this.toggleTab('operator'); 
            }}
            disabled={activeTab === 'operator'} // Optional: disable active tab button
          >
                Operator Anonymization
          </button>
          <button
            type="button"
            className={`eatd-tab-button ${activeTab === 'incident' ? 'is-active' : ''}`}
            onClick={() => {
              this.toggleTab('incident'); 
            }}
            disabled={activeTab === 'incident'} // Optional: disable active tab button
          >
                Incident Processing
          </button>
          <button
            type="button"
            className={`eatd-tab-button ${activeTab === 'person' ? 'is-active' : ''}`}
            onClick={() => {
              this.toggleTab('person'); 
            }}
            disabled={activeTab === 'person'} // Optional: disable active tab button
          >
                Person Processing
          </button>
          <button
            type="button"
            className={`eatd-tab-button ${activeTab === 'change' ? 'is-active' : ''}`}
            onClick={() => {
              this.toggleTab('change');
            }}
            disabled={activeTab === 'change'}
          >
                Change Processing
          </button>
        </div>

        {/* --- Tab Content (using conditional rendering) --- */}
        <div className="eatd-tab-panel">
          {/* --- Operator Tab Content --- */}
          {activeTab === 'operator' && (
            <div>
              <div className="eatd-section-card">
                <div className="form-group">
                  <div className="checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="operatorAnonymizationEnabled"
                        checked={operatorAnonymizationEnabled || false}
                        onChange={this.handleChange}
                      /> Enable Operator Anonymization
                    </label>
                  </div>
                </div>
              </div>
              {this.renderProcessingProfiles('operator', topdeskEndpointId, [], {
                showDateField: false,
                showCleanup: false,
                showStatuses: false,
                showArchivedSettings: true,
                ageLabel: 'Inactive Days Threshold',
                profileDescription: 'Login age, anonymization rules, and the archiving reason to reuse after re-archiving archived operators.',
              })}
            </div>
          )}

          {/* --- Incident Tab Content --- */}
          {activeTab === 'incident' && (
            <div>
              <div className="eatd-section-card">
                <div className="form-group mb-0">
                  <div className="checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="incidentProcessingEnabled"
                        checked={incidentProcessingEnabled || false}
                        onChange={this.handleChange}
                      /> Enable Incident Processing
                    </label>
                  </div>
                </div>
              </div>
              {this.renderProcessingProfiles('incident', topdeskEndpointId, statusOptions)}
            </div>
          )}

          {/* --- Person Tab Content --- */}
          {activeTab === 'person' && (
            <div>
              <div className="eatd-section-card">
                <div className="form-group">
                  <div className="checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="personProcessingEnabled"
                        checked={personProcessingEnabled || false}
                        onChange={this.handleChange}
                      /> Enable Person Processing
                    </label>
                  </div>
                </div>
              </div>
              {this.renderProcessingProfiles('person', topdeskEndpointId, [], {
                showDateField: false,
                showCleanup: false,
                showStatuses: false,
                showArchivedSettings: true,
                ageLabel: 'Inactive Days Threshold',
                profileDescription: 'Age threshold, anonymization rules, and the archiving reason to reuse after re-archiving archived persons.',
              })}
            </div>
          )}

          {/* --- Change Tab Content --- */}
          {activeTab === 'change' && (
            <div>
              <div className="eatd-section-card">
                <div className="form-group mb-0">
                  <div className="checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="changeProcessingEnabled"
                        checked={changeProcessingEnabled || false}
                        onChange={this.handleChange}
                      /> Enable Change Processing
                    </label>
                  </div>
                  <small className="form-text text-muted">
                    Turn on change processing.
                  </small>
                </div>
              </div>
              {this.renderProcessingProfiles('change', topdeskEndpointId, [], {
                showArchivedSettings: true,
                profileDescription: 'Age threshold, cleanup, rules, and archived-change handling per profile.',
              })}
            </div>
          )}
        </div>

        {/* --- Save Button --- */}
        <div className="eatd-save-bar">
          <button type="submit" className="btn btn-success" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    );
  }
}

// Map Redux state to component props
const mapStateToProps = (state, ownProps) => {
  const instanceData = ownProps.instance || _.get(state, 'instance.view.instance', null);
  
  // We only need the endpointsByType from ownProps, following the same pattern as the tester component
  return {
    instance: instanceData,
    isSaving: ownProps.isSetupMode ? _.get(state, 'instance.setup.isSaving', false) : _.get(state, 'instance.settings.isUpdating', false),
    saveError: ownProps.isSetupMode ? _.get(state, 'instance.setup.saveError', null) : _.get(state, 'instance.settings.error', null),
    endpointsByType: ownProps.endpointsByType,
    isSetupMode: ownProps.isSetupMode,
  };
};

// Map Redux actions to component props
const mapDispatchToProps = {
  instanceSave, // Pass the wrapped save action
  instanceSettingsSave,
};

export default connect(mapStateToProps, mapDispatchToProps)(EATDOptionsForm);
